import { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth, apiClient } from "../App";
import { safeCopyToClipboard } from "../lib/utils";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { NumericInput } from "../components/ui/numeric-input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Switch } from "../components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { 
  Table, 
  TableBody, 
  TableCell, 
  TableHead, 
  TableHeader, 
  TableRow 
} from "../components/ui/table";
import { 
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from "../components/ui/dialog";
import { OpsHealthPanel } from "../components/V24Panels";
// OCI V2.8 — OrganisationOnboardingWizard removed (broken/duplicate path). Canonical: Create Organisation dialog.
import { DataManagementCentre } from "../components/DataManagementCentre";
import { EnvironmentBadge } from "../components/EnvironmentBadge";
import { 
  LogOut, 
  Building2, 
  Database,
  Users, 
  TrendingUp,
  Plus,
  CheckCircle2,
  XCircle,
  AlertTriangle,
  Settings,
  Calculator,
  ChevronRight,
  ArrowLeft,
  Ticket,
  Heart,
  Gift,
  PoundSterling,
  Plug,
  Mail,
  MessageSquare,
  MapPin,
  CreditCard,
  Play,
  Loader2,
  Eye,
  EyeOff,
  Download,
  DollarSign,
  Target,
  CheckCircle,
  AlertCircle,
  Pencil,
  Trash2,
  Archive,
  RotateCcw,
  KeyRound,
  UserX,
  LogIn,
  RefreshCw,
  Globe,
  Activity
} from "lucide-react";
import { Checkbox } from "../components/ui/checkbox";
import { Textarea } from "../components/ui/textarea";
import { toast } from "sonner";
import { Badge } from "../components/ui/badge";
import { DialogDescription } from "../components/ui/dialog";

import TapTrackLogo from "../components/TapTrackLogo";
import LiveReadinessCard from "../components/LiveReadinessCard";
import ChurnRadar from "../components/ChurnRadar";
import AccountOpportunities from "../components/AccountOpportunities";
import PlatformCommandCentre from "../components/PlatformCommandCentre";
import CharityPaymentDestinations from "../components/CharityPaymentDestinations";
import LaunchReadiness from "../components/LaunchReadiness";

// Integration service definitions
const INTEGRATION_SERVICES = {
  stripe: {
    name: "Stripe (Payments)",
    icon: CreditCard,
    description: "Process payments and subscriptions",
    color: "text-indigo-600",
    bgColor: "bg-indigo-50",
    configFields: ["publishableKey", "webhookSecret"]
  },
  resend: {
    name: "Resend (Email)",
    icon: Mail,
    description: "Send emails via Resend API",
    color: "text-blue-600",
    bgColor: "bg-blue-50",
    configFields: ["senderEmail"]
  },
  twilio: {
    name: "Twilio (SMS)",
    icon: MessageSquare,
    description: "Send SMS via Twilio",
    color: "text-green-600",
    bgColor: "bg-green-50",
    configFields: ["accountSid", "authToken", "phoneNumber"]
  },
  postcode_lookup: {
    name: "Postcode Lookup",
    icon: MapPin,
    description: "Address lookup by postcode",
    color: "text-amber-600",
    bgColor: "bg-amber-50",
    configFields: ["provider"]
  },
  bank_validation: {
    name: "Bank Validation",
    icon: CreditCard,
    description: "Validate UK bank details",
    color: "text-violet-600",
    bgColor: "bg-violet-50",
    configFields: []
  }
};

export default function PlatformDashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [metrics, setMetrics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("organisations");
  const [showCreateOrg, setShowCreateOrg] = useState(false);
  const [creating, setCreating] = useState(false);
  const [orgFilter, setOrgFilter] = useState("active");
  const [archivingOrgId, setArchivingOrgId] = useState(null);
  const [orgList, setOrgList] = useState([]);
  const [loadingOrgs, setLoadingOrgs] = useState(false);
  // Manual Charge (moved here from Owner Billing; platform_owner only)
  const [manualChargeForm, setManualChargeForm] = useState({ organisationId: "", amount: "", description: "" });
  const [creatingManualCharge, setCreatingManualCharge] = useState(false);
  // OCI V2.5 — Onboarding Wizard
  // OCI V2.8: onboarding wizard removed

  // Users tab state
  const [allUsers, setAllUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [userActionLoading, setUserActionLoading] = useState(null);

  // Enquiries state
  const [enquiries, setEnquiries] = useState([]);
  const [loadingEnquiries, setLoadingEnquiries] = useState(false);
  const [selectedEnquiry, setSelectedEnquiry] = useState(null);
  const [showEnquiryDetail, setShowEnquiryDetail] = useState(false);
  const [selectedEnquiryIds, setSelectedEnquiryIds] = useState([]);
  const [enquiryExportDateFrom, setEnquiryExportDateFrom] = useState("");
  const [enquiryExportDateTo, setEnquiryExportDateTo] = useState("");
  const [showExportModal, setShowExportModal] = useState(false);
  const [enquiryActionLoading, setEnquiryActionLoading] = useState(null);
  
  // Pricing config state
  const [showPricingConfig, setShowPricingConfig] = useState(false);
  const [selectedOrg, setSelectedOrg] = useState(null);
  const [pricingConfig, setPricingConfig] = useState({
    // Pricing mode: "tier" or "custom"
    pricingMode: "tier",
    // One-off donation pricing
    oneOff_pricingType: "percentage",
    oneOff_percentageFee: 0.05,
    oneOff_fixedFee: 0,
    // Custom pricing fields (used when pricingMode == "custom")
    customMonthlyFee: 0,
    customIncludedUsers: 0,
    customExtraUserPrice: 0,
  });
  const [savingPricing, setSavingPricing] = useState(false);
  const [previewAmount, setPreviewAmount] = useState(20);
  
  // Drill-down state
  const [showOrgDetail, setShowOrgDetail] = useState(false);
  const [orgDetail, setOrgDetail] = useState(null);
  const [loadingDetail, setLoadingDetail] = useState(false);
  
  // Metric drill-down modal state
  const [showMetricDrilldown, setShowMetricDrilldown] = useState(false);
  const [drilldownType, setDrilldownType] = useState(null); // 'revenue' | 'conversions' | 'signups'
  const [drilldownData, setDrilldownData] = useState(null);
  const [loadingDrilldown, setLoadingDrilldown] = useState(false);
  const [drilldownDateFilter, setDrilldownDateFilter] = useState("all");
  
  // Integrations state
  const [integrations, setIntegrations] = useState([]);
  const [loadingIntegrations, setLoadingIntegrations] = useState(false);
  const [showIntegrationModal, setShowIntegrationModal] = useState(false);
  const [selectedIntegration, setSelectedIntegration] = useState(null);
  const [integrationForm, setIntegrationForm] = useState({
    service: "",
    apiKey: "",
    config: {}
  });
  const [savingIntegration, setSavingIntegration] = useState(false);
  const [testingIntegration, setTestingIntegration] = useState(null);
  const [showApiKey, setShowApiKey] = useState(false);
  const [testPhoneNumber, setTestPhoneNumber] = useState("");
  
  // Create org form
  const [newOrg, setNewOrg] = useState({
    name: "",
    plan: "basic",
    ownerEmail: "",
    ownerName: "",
    ownerPassword: ""
  });

  // Platform Pricing Control Panel State
  const [pricingPlans, setPricingPlans] = useState([]);
  const [loadingPlans, setLoadingPlans] = useState(false);
  const [pricingDefaults, setPricingDefaults] = useState({
    basicMonthlyFee: 0,
    growthMonthlyFee: 299,
    proMonthlyFee: 999,
    lotterySinglePrice: 1.50,
    lotteryDoublePrice: 2.00,
    regularGivingPrice: 3.00,
    oneOffPercentage: 5
  });
  const [savingDefaults, setSavingDefaults] = useState(false);
  const [initDone, setInitDone] = useState(false);
  const [initLoading, setInitLoading] = useState(false);
  const [resetDone, setResetDone] = useState(false);
  const [resetLoading, setResetLoading] = useState(false);
  // OCI V2.6A — environment gate for development/maintenance tools
  const [platformEnv, setPlatformEnv] = useState(null);

  useEffect(() => {
    document.title = "Engage — Platform";
    apiClient.get("/platform/environment")
      .then((r) => setPlatformEnv((r.data?.environment || "").toUpperCase()))
      .catch(() => setPlatformEnv(null));
  }, []);
  const [landingPricing, setLandingPricing] = useState([
    { name: "Launch", price: 100, users: 10, extraUserPrice: 10, popular: false },
    { name: "Growth", price: 250, users: 30, extraUserPrice: 7.50, popular: true },
    { name: "Pro", price: 500, users: 70, extraUserPrice: 5, popular: false }
  ]);
  const [savingLandingPricing, setSavingLandingPricing] = useState(false);
  const [globalPricingConfig, setGlobalPricingConfig] = useState({ defaultIncludedSignups: 0, defaultPricePerSignup: 0 });
  const [billingOverview, setBillingOverview] = useState([]);
  const [loadingBillingOverview, setLoadingBillingOverview] = useState(false);
  const [showCreatePlanModal, setShowCreatePlanModal] = useState(false);
  const [showEditPlanModal, setShowEditPlanModal] = useState(false);
  const [editingPlan, setEditingPlan] = useState(null);
  const [creatingPlan, setCreatingPlan] = useState(false);
  const [planForm, setPlanForm] = useState({
    name: "",
    monthlyPrice: 0,
    signupsIncluded: 0,
    pricePerSignup: 0,
    maxUsers: 0,
    chargePerUser: false,
    pricePerUser: 0,
    interactionsPerUser: 0,
    extraUserInteractions: 0,
    features: [],
    isActive: true,
    sortOrder: 0,
    smsEnabled: false,
    whiteLabelEnabled: false,
    isPopular: false
  });
  const [featuresInput, setFeaturesInput] = useState("");

  useEffect(() => {
    loadData();
    loadIntegrations();
    loadPricingPlans();
    loadPricingDefaults();
    loadGlobalPricingConfig();
    loadBillingOverview();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    loadOrgs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orgFilter]);

  const loadOrgs = async () => {
    setLoadingOrgs(true);
    try {
      const res = await apiClient.get(`/platform/organisations?filter=${orgFilter}`);
      setOrgList(res.data);
    } catch (e) {
      toast.error("Failed to load organisations");
    } finally {
      setLoadingOrgs(false);
    }
  };

  // Create a one-time Stripe charge for any organisation (platform_owner only)
  const createManualCharge = async () => {
    const { organisationId, amount, description } = manualChargeForm;
    if (!organisationId) { toast.error("Select an organisation"); return; }
    const amt = parseFloat(amount);
    if (!amt || amt <= 0) { toast.error("Enter a valid amount"); return; }
    if (!description.trim()) { toast.error("Enter a description"); return; }
    setCreatingManualCharge(true);
    try {
      const res = await apiClient.post(`/billing/manual-charge/${organisationId}`, {
        amount: amt,
        description: description.trim(),
        originUrl: window.location.origin,
      });
      if (res.data?.url) {
        window.open(res.data.url, "_blank");
        toast.success("Stripe checkout opened in a new tab");
      } else {
        toast.success("Charge created");
      }
      setManualChargeForm({ organisationId: "", amount: "", description: "" });
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to create charge");
    } finally {
      setCreatingManualCharge(false);
    }
  };

  const handleArchiveOrg = async (orgId, orgName) => {
    if (!window.confirm(`Archive "${orgName}"? Users will be blocked from login.`)) return;
    setArchivingOrgId(orgId);
    try {
      await apiClient.put(`/platform/organisations/${orgId}/archive`);
      toast.success(`"${orgName}" archived`);
      loadOrgs();
      loadData();
    } catch (e) {
      toast.error("Failed to archive organisation");
    } finally {
      setArchivingOrgId(null);
    }
  };

  const handleRestoreOrg = async (orgId, orgName) => {
    setArchivingOrgId(orgId);
    try {
      await apiClient.put(`/platform/organisations/${orgId}/restore`);
      toast.success(`"${orgName}" restored`);
      loadOrgs();
      loadData();
    } catch (e) {
      toast.error("Failed to restore organisation");
    } finally {
      setArchivingOrgId(null);
    }
  };

  const handlePurgeOrgData = async (orgId, orgName) => {
    if (!window.confirm(`PERMANENTLY DELETE all data for "${orgName}"? This cannot be undone.`)) return;
    if (!window.confirm(`FINAL WARNING: This will delete all campaigns, conversions, shifts, and users for "${orgName}".`)) return;
    try {
      const res = await apiClient.delete(`/platform/organisations/${orgId}/data`);
      toast.success(`Data purged for "${orgName}"`);
      loadOrgs();
      loadData();
    } catch (e) {
      toast.error("Failed to purge data");
    }
  };

  // Users tab functions
  const loadUsers = async () => {
    setLoadingUsers(true);
    try {
      const res = await apiClient.get("/platform/users");
      setAllUsers(res.data);
    } catch (e) {
      toast.error("Failed to load users");
    } finally {
      setLoadingUsers(false);
    }
  };

  const handleDeactivateUser = async (userId, email) => {
    if (!window.confirm(`Deactivate "${email}"? They will be unable to log in.`)) return;
    setUserActionLoading(userId);
    try {
      await apiClient.put(`/platform/users/${userId}/deactivate`);
      toast.success(`${email} deactivated`);
      loadUsers();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to deactivate user");
    } finally {
      setUserActionLoading(null);
    }
  };

  const handleActivateUser = async (userId, email) => {
    setUserActionLoading(userId);
    try {
      await apiClient.put(`/platform/users/${userId}/activate`);
      toast.success(`${email} activated`);
      loadUsers();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to activate user");
    } finally {
      setUserActionLoading(null);
    }
  };

  const handleDeleteUser = async (userId, email) => {
    if (!window.confirm(`Retire "${email}"? They will be unable to log in. History is preserved and the email can be reactivated later as a new account.`)) return;
    setUserActionLoading(userId);
    try {
      await apiClient.delete(`/platform/users/${userId}`);
      toast.success(`${email} retired (history preserved)`);
      loadUsers();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to retire user");
    } finally {
      setUserActionLoading(null);
    }
  };

  const handleReactivateUser = async (userId, email) => {
    if (!window.confirm(`Reactivate "${email}" as a NEW account on the same email? The retired account stays in history; the new account will need to set a password via the reset link.`)) return;
    setUserActionLoading(userId);
    try {
      const res = await apiClient.post(`/platform/users/${userId}/reactivate`);
      const link = res.data?.resetUrl;
      toast.success(`${email} recreated (v${res.data?.lifecycleVersion}). Password reset link generated.`);
      if (link) window.prompt("Share this password-setup link with the user:", link);
      loadUsers();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to reactivate user");
    } finally {
      setUserActionLoading(null);
    }
  };

  const handleResetPassword = async (userId, email) => {
    setUserActionLoading(userId);
    try {
      const res = await apiClient.post(`/platform/users/${userId}/reset-password`);
      toast.success(`Reset link generated for ${email}`);
      const copied = await safeCopyToClipboard(res.data.resetLink);
      if (copied) toast.info("Reset link copied to clipboard");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to generate reset link");
    } finally {
      setUserActionLoading(null);
    }
  };

  const handleImpersonate = async (userId, email, role) => {
    if (!window.confirm(`Impersonate "${email}"? You will be logged in as this user.`)) return;
    try {
      const res = await apiClient.post(`/platform/users/${userId}/impersonate`);
      sessionStorage.setItem("taptrack_token", res.data.token);
      sessionStorage.setItem("taptrack_user", JSON.stringify(res.data.user));
      sessionStorage.setItem("taptrack_impersonating", "true");
      const path = role === "fundraiser" ? "/dashboard" : role === "owner" ? "/owner" : "/platform";
      window.location.href = path;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to impersonate");
    }
  };

  const loadData = async () => {
    try {
      const response = await apiClient.get("/metrics/platform");
      setMetrics(response.data);
    } catch (error) {
      toast.error("Failed to load platform data");
    } finally {
      setLoading(false);
    }
  };

  const loadIntegrations = async () => {
    setLoadingIntegrations(true);
    try {
      const response = await apiClient.get("/platform/integrations");
      setIntegrations(response.data);
    } catch (error) {
      toast.error("Failed to load integrations");
    } finally {
      setLoadingIntegrations(false);
    }
  };

  // === ENQUIRY MANAGEMENT FUNCTIONS ===

  const loadEnquiries = async () => {
    setLoadingEnquiries(true);
    try {
      const res = await apiClient.get("/platform/enquiries");
      setEnquiries(res.data || []);
    } catch (e) {
      toast.error("Failed to load enquiries");
    } finally {
      setLoadingEnquiries(false);
    }
  };

  const handleEnquiryStatus = async (enquiryId, status) => {
    setEnquiryActionLoading(enquiryId);
    try {
      await apiClient.put(`/platform/enquiries/${enquiryId}/status`, { status });
      toast.success(`Enquiry marked as ${status}`);
      loadEnquiries();
      if (selectedEnquiry?.id === enquiryId) {
        setSelectedEnquiry({ ...selectedEnquiry, status });
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to update enquiry");
    } finally {
      setEnquiryActionLoading(null);
    }
  };

  const handleDeleteEnquiry = async (enquiryId) => {
    if (!window.confirm("Delete this enquiry permanently?")) return;
    setEnquiryActionLoading(enquiryId);
    try {
      await apiClient.delete(`/platform/enquiries/${enquiryId}`);
      toast.success("Enquiry deleted");
      setShowEnquiryDetail(false);
      setSelectedEnquiry(null);
      loadEnquiries();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to delete enquiry");
    } finally {
      setEnquiryActionLoading(null);
    }
  };

  const toggleEnquirySelect = (id) => {
    setSelectedEnquiryIds(prev => 
      prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]
    );
  };

  const toggleAllEnquiries = () => {
    if (selectedEnquiryIds.length === enquiries.length) {
      setSelectedEnquiryIds([]);
    } else {
      setSelectedEnquiryIds(enquiries.map(e => e.id));
    }
  };

  const handleExportEnquiries = (mode) => {
    const params = new URLSearchParams();
    if (mode === "selected" && selectedEnquiryIds.length > 0) {
      params.append("selectedIds", selectedEnquiryIds.join(","));
    } else if (mode === "dateRange") {
      if (enquiryExportDateFrom) params.append("dateFrom", enquiryExportDateFrom);
      if (enquiryExportDateTo) params.append("dateTo", enquiryExportDateTo);
    }
    const url = `${apiClient.defaults.baseURL}/platform/enquiries/export?${params.toString()}`;
    const token = sessionStorage.getItem("taptrack_token");
    // Download via a hidden link with auth header workaround
    fetch(url, { headers: { Authorization: `Bearer ${token}` } })
      .then(res => res.blob())
      .then(blob => {
        const a = document.createElement("a");
        a.href = URL.createObjectURL(blob);
        a.download = `enquiries_export.csv`;
        a.click();
        toast.success("Export downloaded");
        setShowExportModal(false);
      })
      .catch(() => toast.error("Export failed"));
  };

  // === PLATFORM PRICING CONTROL PANEL FUNCTIONS ===
  
  const loadPricingPlans = async () => {
    setLoadingPlans(true);
    try {
      const response = await apiClient.get("/platform/pricing-plans");
      setPricingPlans(response.data || []);
    } catch (error) {
      toast.error("Failed to load pricing plans");
      setPricingPlans([]);
    } finally {
      setLoadingPlans(false);
    }
  };

  const loadPricingDefaults = async () => {
    try {
      const response = await apiClient.get("/platform/pricing-defaults");
      setPricingDefaults(response.data);
      if (response.data.landingPricing?.tiers) {
        setLandingPricing(response.data.landingPricing.tiers);
      }
    } catch (error) {
      toast.error("Failed to load pricing defaults");
    }
  };

  const handleCreatePlan = async () => {
    if (!planForm.name || planForm.monthlyPrice < 0) {
      toast.error("Please fill required fields");
      return;
    }
    
    setCreatingPlan(true);
    try {
      const payload = {
        ...planForm,
        features: featuresInput.split(",").map(f => f.trim()).filter(f => f)
      };
      await apiClient.post("/platform/pricing-plans", payload);
      toast.success("Pricing plan created!");
      setShowCreatePlanModal(false);
      resetPlanForm();
      loadPricingPlans();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create plan");
    } finally {
      setCreatingPlan(false);
    }
  };

  const handleEditPlan = async () => {
    if (!editingPlan || !planForm.name) {
      toast.error("Please fill required fields");
      return;
    }
    
    setCreatingPlan(true);
    try {
      const payload = {
        ...planForm,
        features: featuresInput.split(",").map(f => f.trim()).filter(f => f)
      };
      await apiClient.put(`/platform/pricing-plans/${editingPlan.id}`, payload);
      toast.success("Pricing plan updated!");
      setShowEditPlanModal(false);
      setEditingPlan(null);
      resetPlanForm();
      loadPricingPlans();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update plan");
    } finally {
      setCreatingPlan(false);
    }
  };

  const handleDeletePlan = async (planId, planName) => {
    if (!window.confirm(`Are you sure you want to delete "${planName}"? This cannot be undone.`)) {
      return;
    }
    
    try {
      await apiClient.delete(`/platform/pricing-plans/${planId}`);
      toast.success("Pricing plan deleted");
      loadPricingPlans();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete plan");
    }
  };

  const handleSaveDefaults = async () => {
    setSavingDefaults(true);
    try {
      await apiClient.put("/platform/pricing-defaults", pricingDefaults);
      toast.success("Pricing defaults saved!");
    } catch (error) {
      toast.error("Failed to save defaults");
    } finally {
      setSavingDefaults(false);
    }
  };

  const handleSaveGlobalPricing = async () => {
    setSavingLandingPricing(true);
    try {
      await apiClient.put("/platform/pricing-defaults", {
        ...pricingDefaults,
        landingPricing: { tiers: landingPricing }
      });
      // Also sync to pricing plans so the table above reflects the same values
      await apiClient.post("/platform/sync-pricing-from-global", { tiers: landingPricing });
      // Save global usage pricing defaults
      await apiClient.put("/admin/global-pricing-config", globalPricingConfig);
      await loadPricingPlans();
      toast.success("Global pricing updated!");
    } catch (error) {
      toast.error("Failed to save global pricing");
    } finally {
      setSavingLandingPricing(false);
    }
  };

  const loadGlobalPricingConfig = async () => {
    try {
      const res = await apiClient.get("/admin/global-pricing-config");
      setGlobalPricingConfig(res.data || { defaultIncludedSignups: 0, defaultPricePerSignup: 0 });
    } catch (e) { /* keep defaults */ }
  };

  const loadBillingOverview = async () => {
    setLoadingBillingOverview(true);
    try {
      const res = await apiClient.get("/admin/billing-overview");
      setBillingOverview(res.data || []);
    } catch (e) {
      setBillingOverview([]);
    } finally {
      setLoadingBillingOverview(false);
    }
  };

  const updateLandingTier = (index, field, value) => {
    setLandingPricing(prev => prev.map((t, i) => i === index ? { ...t, [field]: value } : t));
  };

  const openEditPlanModal = (plan) => {
    setEditingPlan(plan);
    setPlanForm({
      name: plan.name || "",
      monthlyPrice: plan.monthlyPrice || 0,
      signupsIncluded: plan.signupsIncluded || 0,
      pricePerSignup: plan.pricePerSignup || 0,
      maxUsers: plan.maxUsers || 0,
      chargePerUser: plan.chargePerUser || false,
      pricePerUser: plan.pricePerUser || 0,
      interactionsPerUser: plan.interactionsPerUser || 0,
      extraUserInteractions: plan.extraUserInteractions || 0,
      features: plan.features || [],
      isActive: plan.isActive !== false,
      sortOrder: plan.sortOrder || 0,
      smsEnabled: plan.smsEnabled || false,
      whiteLabelEnabled: plan.whiteLabelEnabled || false,
      isPopular: plan.isPopular || false
    });
    setFeaturesInput((plan.features || []).join(", "));
    setShowEditPlanModal(true);
  };

  const resetPlanForm = () => {
    setPlanForm({
      name: "",
      monthlyPrice: 0,
      signupsIncluded: 0,
      pricePerSignup: 0,
      maxUsers: 0,
      chargePerUser: false,
      pricePerUser: 0,
      interactionsPerUser: 0,
      extraUserInteractions: 0,
      features: [],
      isActive: true,
      sortOrder: 0,
      smsEnabled: false,
      whiteLabelEnabled: false,
      isPopular: false
    });
    setFeaturesInput("");
  };

  const handleCreateOrg = async () => {
    if (!newOrg.name || !newOrg.ownerEmail || !newOrg.ownerName || !newOrg.ownerPassword) {
      toast.error("Please fill all required fields");
      return;
    }

    setCreating(true);
    try {
      await apiClient.post("/platform/organisations", newOrg);
      toast.success("Organisation created successfully!");
      setShowCreateOrg(false);
      setNewOrg({
        name: "",
        plan: "basic",
        ownerEmail: "",
        ownerName: "",
        ownerPassword: ""
      });
      loadData();
      loadOrgs();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create organisation");
    } finally {
      setCreating(false);
    }
  };

  const openPricingConfig = (org, e) => {
    e?.stopPropagation();
    setSelectedOrg(org);
    setPricingConfig({
      pricingMode: org.pricingMode ?? "tier",
      oneOff_pricingType: org.oneOff_pricingType ?? "percentage",
      oneOff_percentageFee: org.oneOff_percentageFee ?? 0.05,
      oneOff_fixedFee: org.oneOff_fixedFee ?? 0,
      // Custom pricing fields
      customMonthlyFee: org.customMonthlyFee ?? org.monthlyFee ?? 0,
      customIncludedUsers: org.customIncludedUsers ?? org.maxUsers ?? 0,
      customExtraUserPrice: org.customExtraUserPrice ?? org.pricePerUser ?? 0,
      // Per-user pricing
      chargePerUser: org.chargePerUser ?? false,
      pricePerUser: org.pricePerUser ?? 0
    });
    setShowPricingConfig(true);
  };

  const calculatePreview = () => {
    const { oneOff_pricingType, oneOff_percentageFee, oneOff_fixedFee } = pricingConfig;
    const amount = previewAmount;
    
    if (oneOff_pricingType === "fixed") {
      return oneOff_fixedFee;
    } else if (oneOff_pricingType === "hybrid") {
      return Math.max(amount * oneOff_percentageFee, oneOff_fixedFee);
    } else {
      return amount * oneOff_percentageFee;
    }
  };

  const savePricingConfig = async () => {
    if (!selectedOrg) return;
    
    setSavingPricing(true);
    try {
      // Use the pricing-mode endpoint for custom pricing
      await apiClient.put(`/platform/organisations/${selectedOrg.id}/pricing-mode`, {
        pricingMode: pricingConfig.pricingMode,
        customMonthlyFee: pricingConfig.customMonthlyFee,
        customIncludedUsers: pricingConfig.customIncludedUsers,
        customExtraUserPrice: pricingConfig.customExtraUserPrice,
        useCustomPricing: pricingConfig.pricingMode === "custom",
      });
      // Also save one-off pricing config
      await apiClient.put(`/platform/organisations/${selectedOrg.id}/pricing`, {
        oneOff_pricingType: pricingConfig.oneOff_pricingType,
        oneOff_percentageFee: pricingConfig.oneOff_percentageFee,
        oneOff_fixedFee: pricingConfig.oneOff_fixedFee
      });
      toast.success("Pricing configuration saved!");
      setShowPricingConfig(false);
      loadOrgs();
      // Refresh org detail if open so display reflects saved values
      if (showOrgDetail && selectedOrg) {
        try {
          const res = await apiClient.get(`/platform/organisations/${selectedOrg.id}/detail`);
          setOrgDetail(res.data);
        } catch (_) { /* detail refresh is best-effort */ }
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save pricing config");
    } finally {
      setSavingPricing(false);
    }
  };

  // Open organisation detail view (drill-down)
  const openOrgDetail = async (org) => {
    setLoadingDetail(true);
    setShowOrgDetail(true);
    try {
      const res = await apiClient.get(`/platform/organisations/${org.id}/detail`);
      setOrgDetail(res.data);
    } catch (error) {
      toast.error("Failed to load organisation details");
      setShowOrgDetail(false);
    } finally {
      setLoadingDetail(false);
    }
  };

  // Metric drill-down function
  const openMetricDrilldown = async (type) => {
    setDrilldownType(type);
    setShowMetricDrilldown(true);
    setLoadingDrilldown(true);
    
    try {
      // Use existing metrics data to build drill-down
      const orgs = metrics?.organisations || [];
      
      if (type === 'revenue') {
        // Revenue breakdown by organisation
        const breakdown = orgs.map(org => ({
          name: org.name,
          plan: org.plan,
          revenue: org.revenue || 0,
          conversions: org.conversions || 0
        })).sort((a, b) => b.revenue - a.revenue);
        setDrilldownData({ type: 'revenue', items: breakdown, total: metrics?.totalRevenue || 0 });
      } else if (type === 'conversions') {
        // Conversions breakdown
        const breakdown = orgs.map(org => ({
          name: org.name,
          conversions: org.conversions || 0,
          usageCount: org.usageCount || 0
        })).sort((a, b) => b.conversions - a.conversions);
        setDrilldownData({ type: 'conversions', items: breakdown, total: metrics?.totalConversions || 0 });
      } else if (type === 'signups') {
        // Active/Restricted orgs
        const active = orgs.filter(o => o.status === 'active');
        const restricted = orgs.filter(o => o.status === 'restricted');
        setDrilldownData({ 
          type: 'signups', 
          active: active.length, 
          restricted: restricted.length,
          items: orgs.map(org => ({ name: org.name, status: org.status, plan: org.plan }))
        });
      }
    } catch (error) {
      toast.error("Failed to load drill-down data");
    } finally {
      setLoadingDrilldown(false);
    }
  };

  const exportDrilldownData = () => {
    if (!drilldownData?.items) return;
    
    let csvContent = "";
    if (drilldownType === 'revenue') {
      csvContent = "Organisation,Plan,Revenue,Conversions\n";
      drilldownData.items.forEach(item => {
        csvContent += `"${item.name}",${item.plan},${item.revenue},${item.conversions}\n`;
      });
    } else if (drilldownType === 'conversions') {
      csvContent = "Organisation,Conversions,Usage Count\n";
      drilldownData.items.forEach(item => {
        csvContent += `"${item.name}",${item.conversions},${item.usageCount}\n`;
      });
    } else if (drilldownType === 'signups') {
      csvContent = "Organisation,Status,Plan\n";
      drilldownData.items.forEach(item => {
        csvContent += `"${item.name}",${item.status},${item.plan}\n`;
      });
    }
    
    const blob = new Blob([csvContent], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${drilldownType}_breakdown_${new Date().toISOString().split("T")[0]}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success("Data exported");
  };

  // Integration functions
  const openIntegrationModal = (integration) => {
    setSelectedIntegration(integration);
    setIntegrationForm({
      service: integration.service,
      apiKey: "",
      config: integration.config || {}
    });
    setShowApiKey(false);
    setShowIntegrationModal(true);
  };

  const saveIntegration = async () => {
    const isStripe = integrationForm.service === "stripe";
    const isNewConfig = !selectedIntegration?.apiKeyMasked && !selectedIntegration?.config?.stripeSecretKey;
    
    // Validation
    if (isStripe) {
      if (isNewConfig && !integrationForm.config.stripeSecretKey) {
        toast.error("Stripe Secret Key is required");
        return;
      }
    } else {
      if (isNewConfig && !integrationForm.apiKey) {
        toast.error("API key is required for new integration");
        return;
      }
    }

    setSavingIntegration(true);
    try {
      const payload = {
        service: integrationForm.service,
        config: integrationForm.config,
        isActive: true
      };
      
      if (isStripe) {
        // For Stripe: use stripeSecretKey as the apiKey for backward compatibility
        if (integrationForm.config.stripeSecretKey && integrationForm.config.stripeSecretKey.trim()) {
          payload.apiKey = integrationForm.config.stripeSecretKey;
        }
      } else {
        // Only send apiKey if user entered a new one
        if (integrationForm.apiKey && integrationForm.apiKey.trim()) {
          payload.apiKey = integrationForm.apiKey;
        }
      }

      await apiClient.post("/platform/integrations", payload);
      toast.success(`${INTEGRATION_SERVICES[integrationForm.service]?.name || integrationForm.service} configured successfully!`);
      setShowIntegrationModal(false);
      loadIntegrations();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save integration");
    } finally {
      setSavingIntegration(false);
    }
  };

  const toggleIntegration = async (service, currentStatus) => {
    try {
      await apiClient.put(`/platform/integrations/${service}/toggle`);
      toast.success(`Integration ${currentStatus ? 'disabled' : 'enabled'}`);
      loadIntegrations();
    } catch (error) {
      toast.error("Failed to toggle integration");
    }
  };

  const testIntegration = async (service) => {
    setTestingIntegration(service);
    try {
      let payload = {};
      if (service === "twilio") {
        if (!testPhoneNumber) {
          toast.error("Please enter a phone number to test SMS");
          setTestingIntegration(null);
          return;
        }
        payload = { phoneNumber: testPhoneNumber };
      }

      const response = await apiClient.post(`/platform/integrations/${service}/test`, payload);
      
      if (response.data.success) {
        toast.success(response.data.message);
      } else {
        toast.error(response.data.message);
      }
      loadIntegrations();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Test failed");
    } finally {
      setTestingIntegration(null);
    }
  };

  const handleInitSystem = async () => {
    setInitLoading(true);
    try {
      // Backend accepts platform_owner JWT (auto-attached by apiClient). No client-side key needed.
      const res = await apiClient.post("/system/init-demo", {});
      toast.success(`System initialised: ${res.data?.actions?.length || 0} actions`);
      setInitDone(true);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Init failed");
    } finally {
      setInitLoading(false);
    }
  };

  const handleResetAccess = async () => {
    setResetLoading(true);
    try {
      // Backend-only auth (platform_owner JWT, auto-attached). No client-side admin key.
      await apiClient.post("/admin/reset-platform-owner", {});
      toast.success("Platform access reset successfully");
      setResetDone(true);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Reset failed");
    } finally {
      setResetLoading(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="animate-pulse text-slate-600">Loading platform data...</div>
      </div>
    );
  }

  // Organisation Detail View (Drill-down)
  if (showOrgDetail) {
    return (
      <div className="min-h-screen bg-slate-50" data-testid="org-detail-view">
        {/* Header */}
        <header className="bg-white border-b border-slate-200 sticky top-0 z-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setShowOrgDetail(false)}
                className="h-10 px-3"
                data-testid="back-to-platform-btn"
              >
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back
              </Button>
              <div className="border-l border-slate-200 h-6" />
              <div>
                <h1 className="font-heading font-bold text-lg text-slate-900">
                  {orgDetail?.organisation?.name || "Organisation"}
                </h1>
                <p className="text-sm text-slate-500">Organisation Details</p>
              </div>
            </div>
            <TapTrackLogo size="default" />
          </div>
        </header>

        <main className="max-w-7xl mx-auto px-4 sm:px-6 py-6 space-y-6">
          {loadingDetail ? (
            <div className="text-center py-12 text-slate-500">Loading...</div>
          ) : orgDetail ? (
            <>
              {/* Organisation Info */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <Card className="border-slate-200 shadow-none">
                  <CardContent className="p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <Building2 className="h-4 w-4 text-violet-600" />
                      <span className="text-xs font-medium text-slate-500 uppercase tracking-wider">Plan</span>
                    </div>
                    <div className="font-heading font-black text-2xl text-slate-900 capitalize">
                      {orgDetail.organisation.plan}
                    </div>
                    <div className="text-sm text-slate-500 mt-1">
                      {orgDetail.organisation.usageCount} / {orgDetail.organisation.includedUsage} signups
                    </div>
                  </CardContent>
                </Card>
                
                <Card className="border-slate-200 shadow-none">
                  <CardContent className="p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <PoundSterling className="h-4 w-4 text-emerald-600" />
                      <span className="text-xs font-medium text-slate-500 uppercase tracking-wider">Revenue</span>
                    </div>
                    <div className="font-heading font-black text-2xl text-emerald-600">
                      £{orgDetail.metrics.revenue.toLocaleString()}
                    </div>
                    <div className="text-sm text-slate-500 mt-1">
                      Subscription + usage
                    </div>
                  </CardContent>
                </Card>
                
                <Card className="border-slate-200 shadow-none">
                  <CardContent className="p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <TrendingUp className="h-4 w-4 text-blue-600" />
                      <span className="text-xs font-medium text-slate-500 uppercase tracking-wider">Retention</span>
                    </div>
                    <div className="font-heading font-black text-2xl text-blue-600">
                      {(orgDetail.metrics.retentionRate * 100).toFixed(0)}%
                    </div>
                    <div className="text-sm text-slate-500 mt-1">
                      {orgDetail.metrics.activeSignups} active / {orgDetail.metrics.cancelledSignups} cancelled
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Signups by Type */}
              <Card className="border-slate-200 shadow-none">
                <CardContent className="p-4">
                  <h3 className="font-heading font-bold text-slate-900 mb-4">Signups by Type</h3>
                  <div className="grid grid-cols-3 gap-4">
                    <div className="p-4 bg-violet-50 rounded-xl text-center">
                      <Ticket className="h-6 w-6 text-violet-600 mx-auto mb-2" />
                      <div className="font-heading font-bold text-2xl text-violet-600">{orgDetail.metrics.lotteryCount}</div>
                      <div className="text-sm text-violet-700">Lottery</div>
                    </div>
                    <div className="p-4 bg-emerald-50 rounded-xl text-center">
                      <Heart className="h-6 w-6 text-emerald-600 mx-auto mb-2" />
                      <div className="font-heading font-bold text-2xl text-emerald-600">{orgDetail.metrics.regularCount}</div>
                      <div className="text-sm text-emerald-700">Regular Giving</div>
                    </div>
                    <div className="p-4 bg-amber-50 rounded-xl text-center">
                      <Gift className="h-6 w-6 text-amber-600 mx-auto mb-2" />
                      <div className="font-heading font-bold text-2xl text-amber-600">{orgDetail.metrics.oneOffCount}</div>
                      <div className="text-sm text-amber-700">One-Off</div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Owner Info */}
              <Card className="border-slate-200 shadow-none">
                <CardContent className="p-4">
                  <h3 className="font-heading font-bold text-slate-900 mb-3">Owner</h3>
                  <div className="flex items-center gap-4">
                    <div className="w-10 h-10 rounded-full bg-slate-100 flex items-center justify-center">
                      <Users className="h-5 w-5 text-slate-500" />
                    </div>
                    <div>
                      <div className="font-medium text-slate-900">{orgDetail.owner.name || "No owner"}</div>
                      <div className="text-sm text-slate-500">{orgDetail.owner.email || "-"}</div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Pricing Config */}
              <Card className="border-slate-200 shadow-none">
                <CardContent className="p-4">
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="font-heading font-bold text-slate-900">Pricing Configuration</h3>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => openPricingConfig(orgDetail.organisation)}
                      data-testid="edit-pricing-btn"
                    >
                      <Settings className="h-4 w-4 mr-2" />
                      Edit
                    </Button>
                  </div>
                  <div className="grid grid-cols-3 gap-4 text-sm">
                    <div>
                      <div className="text-slate-500">Type</div>
                      <div className="font-medium capitalize">{orgDetail.organisation.oneOff_pricingType}</div>
                    </div>
                    <div>
                      <div className="text-slate-500">Percentage Fee</div>
                      <div className="font-medium">{(orgDetail.organisation.oneOff_percentageFee * 100).toFixed(1)}%</div>
                    </div>
                    <div>
                      <div className="text-slate-500">Fixed Fee</div>
                      <div className="font-medium">£{orgDetail.organisation.oneOff_fixedFee.toFixed(2)}</div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Fundraisers Table */}
              <Card className="border-slate-200 shadow-none">
                <CardContent className="p-0">
                  <div className="p-4 border-b border-slate-200">
                    <h3 className="font-heading font-bold text-slate-900">Fundraisers ({orgDetail.fundraisers.length})</h3>
                  </div>
                  {orgDetail.fundraisers.length > 0 ? (
                    <Table>
                      <TableHeader>
                        <TableRow className="bg-slate-50">
                          <TableHead className="font-semibold">Name</TableHead>
                          <TableHead className="font-semibold">Email</TableHead>
                          <TableHead className="font-semibold text-right">Signups</TableHead>
                          <TableHead className="font-semibold text-right">Active</TableHead>
                          <TableHead className="font-semibold text-right">Retention</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {orgDetail.fundraisers.map((f) => (
                          <TableRow key={f.id}>
                            <TableCell className="font-medium">{f.name}</TableCell>
                            <TableCell className="text-slate-500">{f.email}</TableCell>
                            <TableCell className="text-right">{f.signups}</TableCell>
                            <TableCell className="text-right">{f.active}</TableCell>
                            <TableCell className="text-right">
                              <span className={f.retentionRate >= 0.8 ? 'text-emerald-600' : 'text-amber-600'}>
                                {(f.retentionRate * 100).toFixed(0)}%
                              </span>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  ) : (
                    <div className="p-8 text-center text-slate-500">No fundraisers</div>
                  )}
                </CardContent>
              </Card>

              {/* Campaigns Table */}
              <Card className="border-slate-200 shadow-none">
                <CardContent className="p-0">
                  <div className="p-4 border-b border-slate-200">
                    <h3 className="font-heading font-bold text-slate-900">Campaigns ({orgDetail.campaigns.length})</h3>
                  </div>
                  {orgDetail.campaigns.length > 0 ? (
                    <Table>
                      <TableHeader>
                        <TableRow className="bg-slate-50">
                          <TableHead className="font-semibold">Name</TableHead>
                          <TableHead className="font-semibold text-right">Signups</TableHead>
                          <TableHead className="font-semibold">Status</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {orgDetail.campaigns.map((c) => (
                          <TableRow key={c.id}>
                            <TableCell className="font-medium">{c.name}</TableCell>
                            <TableCell className="text-right">{c.signups}</TableCell>
                            <TableCell>
                              {c.active ? (
                                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs bg-emerald-100 text-emerald-700">
                                  <CheckCircle2 className="h-3 w-3" />
                                  Active
                                </span>
                              ) : (
                                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs bg-slate-100 text-slate-600">
                                  Inactive
                                </span>
                              )}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  ) : (
                    <div className="p-8 text-center text-slate-500">No campaigns</div>
                  )}
                </CardContent>
              </Card>
            </>
          ) : null}
        </main>

        {/* Pricing Config Dialog */}
        <Dialog open={showPricingConfig} onOpenChange={setShowPricingConfig}>
          <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Calculator className="h-5 w-5 text-emerald-600" />
                Organisation Pricing Config
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-6 py-4">
              <div className="bg-slate-50 rounded-lg p-3 mb-2">
                <span className="text-sm text-slate-600">Organisation: </span>
                <span className="font-medium text-slate-900">{selectedOrg?.name}</span>
              </div>

              {/* === PRICING MODE SELECTOR === */}
              <div className="border rounded-lg p-4">
                <h4 className="font-semibold text-slate-900 mb-4">Pricing Mode</h4>
                <div className="space-y-4">
                  <div>
                    <Label className="text-slate-700 font-medium">Select Pricing Mode</Label>
                    <Select 
                      value={pricingConfig.pricingMode} 
                      onValueChange={(v) => setPricingConfig({ ...pricingConfig, pricingMode: v })}
                    >
                      <SelectTrigger className="mt-1.5" data-testid="pricing-mode-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="tier">Use Plan Tier</SelectItem>
                        <SelectItem value="custom">Custom Pricing</SelectItem>
                      </SelectContent>
                    </Select>
                    <p className="text-xs text-slate-500 mt-1.5">
                      {pricingConfig.pricingMode === "tier" 
                        ? "Organisation will use their assigned plan's pricing"
                        : "Organisation will use custom pricing values set below"}
                    </p>
                  </div>

                  {/* Custom Pricing Fields (only show when mode is "custom") */}
                  {pricingConfig.pricingMode === "custom" && (
                    <div className="space-y-4 pt-4 border-t border-slate-200">
                      <div>
                        <Label className="text-slate-700 font-medium">Monthly Fee (£)</Label>
                        <NumericInput
                          step="0.01"
                          min="0"
                          fallback={0}
                          value={pricingConfig.customMonthlyFee}
                          onValueChange={(v) => setPricingConfig({ ...pricingConfig, customMonthlyFee: v })}
                          className="mt-1.5"
                          data-testid="custom-monthly-fee"
                        />
                      </div>
                      <div>
                        <Label className="text-slate-700 font-medium">Included Users</Label>
                        <NumericInput
                          integer
                          step="1"
                          min="0"
                          fallback={0}
                          value={pricingConfig.customIncludedUsers}
                          onValueChange={(v) => setPricingConfig({ ...pricingConfig, customIncludedUsers: v })}
                          className="mt-1.5"
                          data-testid="custom-included-users"
                        />
                      </div>
                      <div>
                        <Label className="text-slate-700 font-medium">Extra User Price (£/month)</Label>
                        <NumericInput
                          step="0.01"
                          min="0"
                          fallback={0}
                          value={pricingConfig.customExtraUserPrice}
                          onValueChange={(v) => setPricingConfig({ ...pricingConfig, customExtraUserPrice: v })}
                          className="mt-1.5"
                          data-testid="custom-extra-user-price"
                        />
                      </div>
                      <div className="bg-emerald-50 rounded-lg p-3 text-xs text-emerald-800 border border-emerald-200">
                        <strong>Custom pricing active.</strong> Monthly fee + per-user overage will be used for billing.
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* === ONE-OFF PRICING SECTION === */}
              <div className="border rounded-lg p-4">
                <h4 className="font-semibold text-slate-900 mb-4">One-Off Donation Pricing</h4>
                
                <div className="space-y-4">
                  <div>
                    <Label className="text-slate-700 font-medium">Pricing Type</Label>
                    <Select 
                      value={pricingConfig.oneOff_pricingType} 
                      onValueChange={(v) => setPricingConfig({ ...pricingConfig, oneOff_pricingType: v })}
                    >
                      <SelectTrigger className="mt-1.5" data-testid="pricing-type-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="percentage">Percentage</SelectItem>
                        <SelectItem value="fixed">Fixed Fee</SelectItem>
                        <SelectItem value="hybrid">Hybrid (Max of % or Fixed)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  {(pricingConfig.oneOff_pricingType === "percentage" || pricingConfig.oneOff_pricingType === "hybrid") && (
                    <div>
                      <Label className="text-slate-700 font-medium">Percentage Fee (%)</Label>
                      <NumericInput
                        step="0.01"
                        min="0"
                        fallback={0}
                        value={Number((pricingConfig.oneOff_percentageFee * 100).toFixed(2))}
                        onValueChange={(v) => setPricingConfig({ ...pricingConfig, oneOff_percentageFee: v === "" ? "" : v / 100 })}
                        className="mt-1.5"
                      />
                    </div>
                  )}

                  {(pricingConfig.oneOff_pricingType === "fixed" || pricingConfig.oneOff_pricingType === "hybrid") && (
                    <div>
                      <Label className="text-slate-700 font-medium">Fixed Fee (£)</Label>
                      <NumericInput
                        step="0.01"
                        min="0"
                        fallback={0}
                        value={pricingConfig.oneOff_fixedFee}
                        onValueChange={(v) => setPricingConfig({ ...pricingConfig, oneOff_fixedFee: v })}
                        className="mt-1.5"
                      />
                    </div>
                  )}

                  <div className="bg-emerald-50 rounded-lg p-4 border border-emerald-200">
                    <div className="flex items-center gap-2 mb-3">
                      <Calculator className="h-4 w-4 text-emerald-600" />
                      <span className="font-medium text-emerald-700">Fee Preview</span>
                    </div>
                    <div className="flex items-center gap-2 mb-2">
                      <span className="text-sm text-slate-600">If donation is £</span>
                      <NumericInput
                        fallback={0}
                        value={previewAmount}
                        onValueChange={(v) => setPreviewAmount(v)}
                        className="w-20 h-8 text-center"
                      />
                    </div>
                    <div className="text-lg font-heading font-bold text-emerald-700">
                      → Fee = £{calculatePreview().toFixed(2)}
                    </div>
                  </div>
                </div>
              </div>

              <Button
                onClick={savePricingConfig}
                disabled={savingPricing}
                className="w-full bg-emerald-500 hover:bg-emerald-600"
              >
                {savingPricing ? "Saving..." : "Save Pricing Configuration"}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    );
  }

  // Main Platform Dashboard
  const summaryStats = [
    { 
      label: "Total Revenue", 
      value: `£${metrics?.totalRevenue?.toLocaleString() || 0}`,
      icon: PoundSterling,
      color: "text-emerald-600"
    },
    { 
      label: "Total Conversions", 
      value: metrics?.totalConversions || 0,
      icon: TrendingUp,
      color: "text-blue-600"
    },
    { 
      label: "Organisations", 
      value: metrics?.totalOrganisations || 0,
      icon: Building2,
      color: "text-violet-600"
    },
    { 
      label: "Active Orgs", 
      value: metrics?.activeOrganisations || 0,
      icon: CheckCircle2,
      color: "text-emerald-600"
    },
    { 
      label: "Restricted Orgs", 
      value: metrics?.restrictedOrganisations || 0,
      icon: AlertTriangle,
      color: "text-amber-600"
    }
  ];

  const getPlanBadgeColor = (plan) => {
    switch (plan) {
      case "enterprise": return "bg-violet-100 text-violet-700";
      case "pro": return "bg-blue-100 text-blue-700";
      default: return "bg-slate-100 text-slate-700";
    }
  };

  const getStatusBadge = (status) => {
    if (status === "active") {
      return (
        <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-emerald-100 text-emerald-700">
          <CheckCircle2 className="h-3 w-3" />
          Active
        </span>
      );
    }
    return (
      <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-700">
        <XCircle className="h-3 w-3" />
        Restricted
      </span>
    );
  };

  return (
    <div className="min-h-screen bg-slate-50" data-testid="platform-dashboard">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <TapTrackLogo size="default" />
            <div className="h-6 w-px bg-slate-200" />
            <span className="text-sm font-medium text-slate-500">Platform Admin</span>
          </div>
          <div className="flex items-center gap-2 relative z-50">
            <span className="text-sm text-slate-500 hidden sm:inline">{user?.email}</span>
            <Button
              variant="outline"
              size="sm"
              onClick={() => navigate("/operational-record")}
              className="h-8 px-3 text-xs border-emerald-200 text-emerald-700"
              data-testid="op-record-entry-btn"
              title="Operational Record is scoped to a single organisation. Fleet-level data lives here on the Platform Dashboard."
            >
              <Activity className="h-4 w-4 mr-1.5" />
              <span className="hidden sm:inline">Org Operational Record</span>
            </Button>
            <EnvironmentBadge size="lg" />
            <Button
              variant="ghost"
              size="sm"
              onClick={handleLogout}
              className="h-10 px-3 text-slate-500 relative"
              data-testid="logout-btn"
            >
              <LogOut className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </header>

      {/* Main */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-6 space-y-6">
        {/* Platform Command Centre — 4-row layout (P0) */}
        <PlatformCommandCentre onNavigateToIntegrations={() => {
          setActiveTab("integrations");
          setTimeout(() => {
            const el = document.querySelector('[data-testid="tab-integrations"]') || document.querySelector('[data-testid="integrations-panel"]');
            el?.scrollIntoView({ behavior: "smooth", block: "start" });
          }, 150);
        }} />

        {/* Summary Stats - Now Clickable */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4" data-testid="summary-stats">
          {summaryStats.map((stat, index) => {
            const isClickable = ['Total Revenue', 'Total Conversions', 'Organisations'].includes(stat.label);
            const drilldownKey = stat.label === 'Total Revenue' ? 'revenue' 
              : stat.label === 'Total Conversions' ? 'conversions' 
              : stat.label === 'Organisations' ? 'signups' : null;
            
            return (
              <Card 
                key={stat.label || index} 
                className={`border-slate-200 shadow-none ${isClickable ? 'cursor-pointer hover:border-blue-300 hover:shadow-md transition-all' : ''}`}
                onClick={() => isClickable && drilldownKey && openMetricDrilldown(drilldownKey)}
              >
                <CardContent className="p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <stat.icon className={`h-4 w-4 ${stat.color}`} />
                    <span className="text-xs font-medium text-slate-500 uppercase tracking-wider">
                      {stat.label}
                    </span>
                    {isClickable && (
                      <ChevronRight className="h-3 w-3 text-slate-400 ml-auto" />
                    )}
                  </div>
                  <div className="font-heading font-black text-2xl text-slate-900">
                    {stat.value}
                  </div>
                  {isClickable && (
                    <div className="text-xs text-blue-500 mt-1">Click for breakdown</div>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* Tabs: Organisations | Pricing | Integrations */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
          <TabsList className="bg-white border border-slate-200">
            <TabsTrigger value="organisations" className="data-[state=active]:bg-slate-100" data-testid="tab-organisations">
              <Building2 className="h-4 w-4 mr-2" />
              Organisations
            </TabsTrigger>
            <TabsTrigger value="pricing" className="data-[state=active]:bg-slate-100" data-testid="tab-pricing">
              <DollarSign className="h-4 w-4 mr-2" />
              Pricing
            </TabsTrigger>
            <TabsTrigger value="integrations" className="data-[state=active]:bg-slate-100" data-testid="tab-integrations">
              <Plug className="h-4 w-4 mr-2" />
              Integrations
            </TabsTrigger>
            <TabsTrigger value="users" className="data-[state=active]:bg-slate-100" data-testid="tab-users" onClick={() => { if (allUsers.length === 0) loadUsers(); }}>
              <Users className="h-4 w-4 mr-2" />
              Users
            </TabsTrigger>
            <TabsTrigger value="enquiries" className="data-[state=active]:bg-slate-100" data-testid="tab-enquiries" onClick={() => { if (enquiries.length === 0) loadEnquiries(); }}>
              <MessageSquare className="h-4 w-4 mr-2" />
              Enquiries
            </TabsTrigger>
            <TabsTrigger value="ops-health" className="data-[state=active]:bg-slate-100" data-testid="tab-ops-health">
              <Plug className="h-4 w-4 mr-2" />
              Operations Health
            </TabsTrigger>
            <TabsTrigger value="data-mgmt" className="data-[state=active]:bg-slate-100" data-testid="tab-data-mgmt">
              <Database className="h-4 w-4 mr-2" />
              Data Management
            </TabsTrigger>
            <TabsTrigger value="payment-destinations" className="data-[state=active]:bg-slate-100" data-testid="tab-payment-destinations">
              <DollarSign className="h-4 w-4 mr-2" />
              Payment Destinations
            </TabsTrigger>
            {platformEnv && platformEnv !== "PRODUCTION" && (
              <TabsTrigger value="maintenance" className="data-[state=active]:bg-slate-100" data-testid="tab-maintenance">
                <Settings className="h-4 w-4 mr-2" />
                Maintenance
              </TabsTrigger>
            )}
          </TabsList>

          {/* OCI V2.6A — Maintenance / Development Tools (hidden entirely in PRODUCTION) */}
          {platformEnv && platformEnv !== "PRODUCTION" && (
            <TabsContent value="maintenance" className="space-y-4" data-testid="maintenance-panel">
              <Card className="border-amber-200">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-base">
                    <Settings className="h-4 w-4 text-amber-600" />
                    Development Tools
                  </CardTitle>
                  <p className="text-sm text-slate-500">
                    These are development & recovery tools. They are disabled and hidden in
                    PRODUCTION. Current environment: <span className="font-semibold">{platformEnv}</span>.
                  </p>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex items-start justify-between gap-4 border-b border-slate-100 pb-4">
                    <div>
                      <div className="font-medium text-slate-800">Initialise System</div>
                      <p className="text-sm text-slate-500">
                        Seeds demo organisation, campaigns, conversions, payroll & training. Idempotent.
                      </p>
                    </div>
                    <Button variant="outline" size="sm" onClick={handleInitSystem} disabled={initDone || initLoading} data-testid="init-system-btn">
                      {initLoading ? "Initialising..." : initDone ? "Initialised" : "Initialise System"}
                    </Button>
                  </div>
                  <div className="flex items-start justify-between gap-4">
                    <div>
                      <div className="font-medium text-slate-800">Reset Platform Access</div>
                      <p className="text-sm text-slate-500">
                        Resets the platform owner password to the configured environment value.
                      </p>
                    </div>
                    <Button variant="outline" size="sm" onClick={handleResetAccess} disabled={resetDone || resetLoading} data-testid="reset-access-btn">
                      {resetLoading ? "Resetting..." : resetDone ? "Access Reset" : "Reset Platform Access"}
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>
          )}

          {/* OCI V2.6 — Data Management Centre */}
          <TabsContent value="data-mgmt" className="space-y-4">
            <DataManagementCentre />
          </TabsContent>

          {/* OCI V2.17B Step 2 — Charity Payment Destinations master registry */}
          <TabsContent value="payment-destinations" className="space-y-4" data-testid="payment-destinations-panel">
            <LaunchReadiness />
            <CharityPaymentDestinations />
          </TabsContent>

          {/* Operations Health Tab — V2.4 */}
          <TabsContent value="ops-health" className="space-y-4">
            <OpsHealthPanel />
          </TabsContent>

          {/* Organisations Tab */}
          <TabsContent value="organisations" className="space-y-4">
            {/* OCI V2.8 — Onboarding wizard removed (broken/duplicate). Canonical path:
                the "Create Organisation" dialog below (single source of truth). */}
            {/* Manual Charge (moved from Owner Billing; platform_owner only) */}
            <Card className="border-slate-200 shadow-none" data-testid="platform-manual-charge-card">
              <CardContent className="p-5">
                <div className="flex items-start justify-between gap-3 mb-3">
                  <div>
                    <h3 className="font-heading font-bold text-slate-900">Manual Charge</h3>
                    <p className="text-xs text-slate-500">Create a one-time Stripe payment for any organisation.</p>
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-12 gap-3 items-end">
                  <div className="md:col-span-4">
                    <Label className="text-xs text-slate-600">Organisation</Label>
                    <select
                      value={manualChargeForm.organisationId}
                      onChange={(e) => setManualChargeForm((p) => ({ ...p, organisationId: e.target.value }))}
                      className="mt-1 h-10 w-full rounded-md border border-slate-200 bg-white px-3 text-sm"
                      data-testid="manual-charge-org-select"
                    >
                      <option value="">Select organisation...</option>
                      {(orgList || []).map((o) => (
                        <option key={o.id} value={o.id}>{o.name}</option>
                      ))}
                    </select>
                  </div>
                  <div className="md:col-span-2">
                    <Label className="text-xs text-slate-600">Amount (£)</Label>
                    <Input
                      type="number" step="0.01" min="0.01"
                      value={manualChargeForm.amount}
                      onChange={(e) => setManualChargeForm((p) => ({ ...p, amount: e.target.value }))}
                      className="mt-1"
                      data-testid="manual-charge-amount"
                      placeholder="0.00"
                    />
                  </div>
                  <div className="md:col-span-4">
                    <Label className="text-xs text-slate-600">Description</Label>
                    <Input
                      value={manualChargeForm.description}
                      onChange={(e) => setManualChargeForm((p) => ({ ...p, description: e.target.value }))}
                      className="mt-1"
                      data-testid="manual-charge-description"
                      placeholder="e.g. Setup fee"
                    />
                  </div>
                  <div className="md:col-span-2">
                    <Button
                      onClick={createManualCharge}
                      disabled={creatingManualCharge}
                      className="w-full bg-blue-600 hover:bg-blue-700 text-white"
                      data-testid="manual-charge-create-btn"
                    >
                      {creatingManualCharge ? "Creating..." : "Create Charge"}
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="border-slate-200 shadow-none">
              <CardContent className="p-0">
                <div className="p-4 border-b border-slate-200 flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <h3 className="font-heading font-bold text-slate-900">Organisation Overview</h3>
                    <div className="flex bg-slate-100 rounded-md p-0.5" data-testid="org-filter-toggle">
                      <button
                        onClick={() => setOrgFilter("active")}
                        className={`px-3 py-1 text-xs font-medium rounded transition-colors ${orgFilter === "active" ? "bg-white text-slate-900 shadow-sm" : "text-slate-500 hover:text-slate-700"}`}
                        data-testid="org-filter-active"
                      >
                        Active
                      </button>
                      <button
                        onClick={() => setOrgFilter("archived")}
                        className={`px-3 py-1 text-xs font-medium rounded transition-colors ${orgFilter === "archived" ? "bg-white text-slate-900 shadow-sm" : "text-slate-500 hover:text-slate-700"}`}
                        data-testid="org-filter-archived"
                      >
                        Archived
                      </button>
                    </div>
                  </div>
                  <Dialog open={showCreateOrg} onOpenChange={setShowCreateOrg}>
                    <DialogTrigger asChild>
                      <Button 
                        className="bg-emerald-500 hover:bg-emerald-600 disabled:opacity-50" 
                        data-testid="create-org-btn"
                        disabled={pricingPlans.length === 0}
                        title={pricingPlans.length === 0 ? "Create pricing plans first" : "Create a new organisation"}
                      >
                        <Plus className="h-4 w-4 mr-2" />
                        Create Organisation
                      </Button>
                    </DialogTrigger>
                    <DialogContent className="sm:max-w-md">
                      <DialogHeader>
                        <DialogTitle>Create Organisation</DialogTitle>
                      </DialogHeader>
                      {/* EMPTY STATE CHECK - Block creation if no plans exist */}
                      {pricingPlans.length === 0 ? (
                        <div className="py-8 text-center">
                          <AlertCircle className="h-12 w-12 text-amber-500 mx-auto mb-4" />
                          <h4 className="font-semibold text-slate-900 mb-2">No Pricing Plans Created</h4>
                          <p className="text-sm text-slate-500 mb-4">
                            You must create at least one pricing plan before creating organisations.
                          </p>
                          <Button
                            variant="outline"
                            onClick={() => { setShowCreateOrg(false); setActiveTab("pricing"); }}
                          >
                            Go to Pricing Tab
                          </Button>
                        </div>
                      ) : (
                        <div className="space-y-4 py-4">
                          <div>
                            <Label>Organisation Name *</Label>
                            <Input
                              value={newOrg.name}
                              onChange={(e) => setNewOrg({ ...newOrg, name: e.target.value })}
                              placeholder="Acme Charity"
                              className="mt-1.5"
                              data-testid="org-name-input"
                            />
                          </div>
                          <div>
                            <Label>Plan *</Label>
                            <Select value={newOrg.plan} onValueChange={(v) => setNewOrg({ ...newOrg, plan: v })}>
                              <SelectTrigger className="mt-1.5" data-testid="org-plan-select">
                                <SelectValue placeholder="Select a plan" />
                              </SelectTrigger>
                              <SelectContent>
                                {pricingPlans.filter(p => p.isActive !== false).map((plan) => (
                                  <SelectItem key={plan.id} value={plan.id}>
                                    {plan.name} (£{plan.monthlyPrice}/mo - {plan.maxUsers} users)
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                          <div className="pt-2 border-t">
                            <Label className="text-slate-500 text-xs uppercase tracking-wider">Owner Account</Label>
                          </div>
                          <div>
                            <Label>Owner Name *</Label>
                            <Input
                              value={newOrg.ownerName}
                              onChange={(e) => setNewOrg({ ...newOrg, ownerName: e.target.value })}
                              placeholder="John Smith"
                              className="mt-1.5"
                            />
                          </div>
                          <div>
                            <Label>Owner Email *</Label>
                            <Input
                              type="email"
                              value={newOrg.ownerEmail}
                              onChange={(e) => setNewOrg({ ...newOrg, ownerEmail: e.target.value })}
                              placeholder="owner@company.com"
                              className="mt-1.5"
                            />
                          </div>
                          <div>
                            <Label>Owner Password *</Label>
                            <Input
                              type="password"
                              value={newOrg.ownerPassword}
                              onChange={(e) => setNewOrg({ ...newOrg, ownerPassword: e.target.value })}
                              placeholder="Secure password"
                              className="mt-1.5"
                            />
                          </div>
                          <Button
                            onClick={handleCreateOrg}
                            disabled={creating || !newOrg.plan}
                            className="w-full bg-emerald-500 hover:bg-emerald-600"
                          >
                            {creating ? "Creating..." : "Create Organisation & Owner"}
                          </Button>
                        </div>
                      )}
                    </DialogContent>
                  </Dialog>
                </div>
                
                {orgList.length > 0 ? (
                  <div className="overflow-x-auto">
                    <Table data-testid="organisation-table">
                      <TableHeader>
                        <TableRow className="bg-slate-50">
                          <TableHead className="font-semibold">Organisation</TableHead>
                          <TableHead className="font-semibold">Plan</TableHead>
                          <TableHead className="font-semibold">Status</TableHead>
                          <TableHead className="font-semibold text-right">Usage</TableHead>
                          <TableHead className="font-semibold text-right">Included</TableHead>
                          <TableHead className="font-semibold text-right">Revenue</TableHead>
                          <TableHead className="font-semibold">Created</TableHead>
                          <TableHead className="font-semibold">Actions</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {orgList.map((org) => (
                          <TableRow 
                            key={org.id}
                            className={`hover:bg-slate-50 transition-colors ${org.isArchived ? "opacity-60" : "cursor-pointer"}`}
                            onClick={() => !org.isArchived && openOrgDetail(org)}
                            data-testid={`org-row-${org.id}`}
                          >
                            <TableCell>
                              <div className="flex items-center gap-2">
                                <span className="font-medium">{org.name}</span>
                                {org.isArchived && (
                                  <span className="text-[10px] font-semibold bg-slate-200 text-slate-600 px-1.5 py-0.5 rounded">ARCHIVED</span>
                                )}
                              </div>
                              {org.ownerEmail && <div className="text-xs text-slate-400">{org.ownerEmail}</div>}
                            </TableCell>
                            <TableCell>
                              <span className={`inline-flex px-2 py-1 rounded-full text-xs font-medium capitalize ${getPlanBadgeColor(org.plan)}`}>
                                {org.plan}
                              </span>
                            </TableCell>
                            <TableCell>
                              {org.isArchived ? (
                                <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-slate-100 text-slate-600">
                                  <Archive className="h-3 w-3" /> Archived
                                </span>
                              ) : getStatusBadge(org.status)}
                            </TableCell>
                            <TableCell className="text-right">
                              <span className={org.usageCount >= org.includedUsage ? 'text-red-600 font-medium' : ''}>
                                {org.usageCount}
                              </span>
                            </TableCell>
                            <TableCell className="text-right text-slate-500">{org.includedUsage}</TableCell>
                            <TableCell className="text-right font-medium">£{org.revenue}</TableCell>
                            <TableCell className="text-sm text-slate-500">
                              {org.createdAt ? new Date(org.createdAt).toLocaleDateString() : '-'}
                            </TableCell>
                            <TableCell>
                              <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
                                {org.isArchived ? (
                                  <>
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      onClick={() => handleRestoreOrg(org.id, org.name)}
                                      disabled={archivingOrgId === org.id}
                                      className="h-7 text-xs text-emerald-600 hover:text-emerald-700 hover:bg-emerald-50"
                                      data-testid={`restore-org-${org.id}`}
                                    >
                                      <RotateCcw className="h-3 w-3 mr-1" /> Restore
                                    </Button>
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      onClick={() => handlePurgeOrgData(org.id, org.name)}
                                      className="h-7 text-xs text-red-500 hover:text-red-700 hover:bg-red-50"
                                      data-testid={`purge-org-${org.id}`}
                                    >
                                      <Trash2 className="h-3 w-3 mr-1" /> Purge
                                    </Button>
                                  </>
                                ) : (
                                  <>
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      onClick={(e) => openPricingConfig(org, e)}
                                      className="h-7 w-7 p-0"
                                    >
                                      <Settings className="h-3.5 w-3.5 text-slate-500" />
                                    </Button>
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      onClick={() => handleArchiveOrg(org.id, org.name)}
                                      disabled={archivingOrgId === org.id}
                                      className="h-7 text-xs text-slate-500 hover:text-red-600 hover:bg-red-50"
                                      data-testid={`archive-org-${org.id}`}
                                    >
                                      <Archive className="h-3 w-3 mr-1" /> Archive
                                    </Button>
                                  </>
                                )}
                              </div>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                ) : (
                  <div className="p-8 text-center text-slate-500">
                    <Building2 className="h-12 w-12 mx-auto mb-3 text-slate-300" />
                    <p>{orgFilter === "archived" ? "No archived organisations" : "No organisations yet"}</p>
                    {orgFilter === "active" && <p className="text-sm mt-1">Create your first organisation to get started</p>}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* === PRICING CONTROL PANEL TAB === */}
          <TabsContent value="pricing" className="space-y-6" data-testid="pricing-panel">
            {/* Section 1: Pricing Plans */}
            <Card className="border-slate-200 shadow-none">
              <CardContent className="p-0">
                <div className="p-4 border-b border-slate-200 flex items-center justify-between">
                  <div>
                    <h3 className="font-heading font-bold text-slate-900">Pricing Plans</h3>
                    <p className="text-sm text-slate-500">Define subscription plans for organisations</p>
                  </div>
                  <Button 
                    onClick={() => { resetPlanForm(); setShowCreatePlanModal(true); }}
                    className="bg-emerald-500 hover:bg-emerald-600"
                    data-testid="create-plan-btn"
                  >
                    <Plus className="h-4 w-4 mr-2" />
                    Create Pricing Plan
                  </Button>
                </div>
                
                {/* Plans Table or Empty State */}
                {loadingPlans ? (
                  <div className="p-8 text-center text-slate-500">Loading pricing plans...</div>
                ) : pricingPlans.length === 0 ? (
                  <div className="p-12 text-center">
                    <DollarSign className="h-12 w-12 text-slate-300 mx-auto mb-4" />
                    <h4 className="font-semibold text-slate-700 mb-2">No Pricing Plans Created</h4>
                    <p className="text-sm text-slate-500 mb-4">
                      Create your first pricing plan to start onboarding organisations.
                    </p>
                    <Button 
                      onClick={() => { resetPlanForm(); setShowCreatePlanModal(true); }}
                      variant="outline"
                    >
                      <Plus className="h-4 w-4 mr-2" />
                      Create First Plan
                    </Button>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow className="bg-slate-50">
                          <TableHead>Plan Name</TableHead>
                          <TableHead className="text-right">Monthly Price</TableHead>
                          <TableHead className="text-right">Included Users</TableHead>
                          <TableHead className="text-right">Interactions / User</TableHead>
                          <TableHead className="text-right">Total Included</TableHead>
                          <TableHead className="text-right">Extra User Price</TableHead>
                          <TableHead>Status</TableHead>
                          <TableHead className="text-right">Actions</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {pricingPlans.map((plan) => {
                          const includedUsers = plan.maxUsers || 0;
                          const interactionsPerUser = plan.interactionsPerUser || 0;
                          const totalIncluded = includedUsers * interactionsPerUser;
                          return (
                          <TableRow key={plan.id}>
                            <TableCell className="font-semibold text-slate-900">
                              {plan.name}
                              {plan.isPopular && (
                                <span className="ml-2 px-1.5 py-0.5 bg-amber-100 text-amber-700 text-xs font-medium rounded-full">Popular</span>
                              )}
                            </TableCell>
                            <TableCell className="text-right font-mono">
                              £{(plan.monthlyPrice || 0).toFixed(2)}
                            </TableCell>
                            <TableCell className="text-right">{includedUsers}</TableCell>
                            <TableCell className="text-right">{interactionsPerUser}</TableCell>
                            <TableCell className="text-right font-mono text-emerald-700">{totalIncluded.toLocaleString()}</TableCell>
                            <TableCell className="text-right font-mono">
                              £{(plan.pricePerUser || 0).toFixed(2)}
                            </TableCell>
                            <TableCell>
                              {plan.isActive !== false ? (
                                <span className="px-2 py-1 bg-emerald-100 text-emerald-700 text-xs font-medium rounded-full">
                                  Active
                                </span>
                              ) : (
                                <span className="px-2 py-1 bg-slate-100 text-slate-600 text-xs font-medium rounded-full">
                                  Inactive
                                </span>
                              )}
                            </TableCell>
                            <TableCell>
                              <div className="flex items-center justify-end gap-1">
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => openEditPlanModal(plan)}
                                  data-testid={`edit-plan-${plan.id}`}
                                >
                                  <Pencil className="h-4 w-4" />
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                  onClick={() => handleDeletePlan(plan.id, plan.name)}
                                  data-testid={`delete-plan-${plan.id}`}
                                >
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                              </div>
                            </TableCell>
                          </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Section 2: Client Billing Overview (Global Pricing Controls removed — single source of truth is Pricing Plans above) */}
            <Card className="border-slate-200 shadow-none" data-testid="client-billing-overview">
              <CardContent className="p-0">
                <div className="p-4 border-b border-slate-200 flex items-center justify-between">
                  <div>
                    <h3 className="font-heading font-bold text-slate-900">Client Billing Overview</h3>
                    <p className="text-sm text-slate-500">Live usage-based billing for all organisations</p>
                  </div>
                  <Button variant="outline" size="sm" onClick={loadBillingOverview} data-testid="refresh-billing-overview">Refresh</Button>
                </div>
                <div className="p-4">
                  {loadingBillingOverview ? (
                    <div className="text-center py-8 text-slate-400">Loading billing data...</div>
                  ) : billingOverview.length === 0 ? (
                    <p className="text-sm text-slate-400 text-center py-8">No organisations found</p>
                  ) : (
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm" data-testid="billing-overview-table">
                        <thead>
                          <tr className="border-b text-left text-slate-500">
                            <th className="pb-2 pr-4">Organisation</th>
                            <th className="pb-2 pr-4">Plan</th>
                            <th className="pb-2 pr-4 text-right">Total Signups</th>
                            <th className="pb-2 pr-4 text-right">Billable</th>
                            <th className="pb-2 pr-4 text-right">Usage £</th>
                            <th className="pb-2 text-right">Total £</th>
                          </tr>
                        </thead>
                        <tbody>
                          {billingOverview.filter(o => o.totalSignups > 0 || o.basePrice > 0).map(o => (
                            <tr key={o.organisationId} className="border-b border-slate-100">
                              <td className="py-2 pr-4 font-medium text-slate-800">{o.organisationName}</td>
                              <td className="py-2 pr-4 text-slate-600">{o.planName}</td>
                              <td className="py-2 pr-4 text-right">{o.totalSignups}</td>
                              <td className="py-2 pr-4 text-right text-amber-600">{o.billableSignups}</td>
                              <td className="py-2 pr-4 text-right text-amber-600">£{o.usageCharge?.toFixed(2)}</td>
                              <td className="py-2 text-right font-bold text-emerald-700">£{o.totalEstimated?.toFixed(2)}</td>
                            </tr>
                          ))}
                          <tr className="border-t-2 border-slate-300 font-bold">
                            <td className="py-2 pr-4" colSpan="2">TOTAL</td>
                            <td className="py-2 pr-4 text-right">{billingOverview.reduce((s, o) => s + (o.totalSignups || 0), 0)}</td>
                            <td className="py-2 pr-4 text-right text-amber-600">{billingOverview.reduce((s, o) => s + (o.billableSignups || 0), 0)}</td>
                            <td className="py-2 pr-4 text-right text-amber-600">£{billingOverview.reduce((s, o) => s + (o.usageCharge || 0), 0).toFixed(2)}</td>
                            <td className="py-2 text-right text-emerald-700">£{billingOverview.reduce((s, o) => s + (o.totalEstimated || 0), 0).toFixed(2)}</td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </TabsContent>
          <TabsContent value="integrations" className="space-y-4" data-testid="integrations-panel">
            {/* Warning if email not configured */}
            {integrations && !integrations.find(i => i.service === 'resend' && i.isActive) && (
              <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 flex items-start gap-3">
                <AlertCircle className="h-5 w-5 text-amber-600 mt-0.5 flex-shrink-0" />
                <div>
                  <h4 className="font-semibold text-amber-800">Email Integration Not Configured</h4>
                  <p className="text-sm text-amber-700 mt-1">
                    Email notifications (Resend) are not active. Users will not receive confirmation emails. 
                    Configure the Resend API key below to enable email features.
                  </p>
                </div>
              </div>
            )}
            
            <Card className="border-slate-200 shadow-none">
              <CardContent className="p-4 border-b border-slate-200">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="font-heading font-bold text-slate-900">Platform Integrations</h3>
                    <p className="text-sm text-slate-500 mt-1">Configure external service API keys</p>
                  </div>
                </div>
              </CardContent>
              
              {loadingIntegrations ? (
                <div className="p-8 text-center text-slate-500">Loading integrations...</div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 p-4">
                  {Object.entries(INTEGRATION_SERVICES).map(([serviceKey, serviceDef]) => {
                    const integration = integrations.find(i => i.service === serviceKey);
                    const isConfigured = integration?.apiKeyMasked && integration.apiKeyMasked !== "";
                    const isActive = integration?.isActive || false;
                    const Icon = serviceDef.icon;
                    
                    return (
                      <Card key={serviceKey} className="border-slate-200 shadow-none">
                        <CardContent className="p-4">
                          <div className="flex items-start justify-between mb-3">
                            <div className="flex items-center gap-3">
                              <div className={`w-10 h-10 rounded-lg ${serviceDef.bgColor} flex items-center justify-center`}>
                                <Icon className={`h-5 w-5 ${serviceDef.color}`} />
                              </div>
                              <div>
                                <h4 className="font-semibold text-slate-900">{serviceDef.name}</h4>
                                <p className="text-xs text-slate-500">{serviceDef.description}</p>
                              </div>
                            </div>
                            {isConfigured && (
                              <Switch
                                checked={isActive}
                                onCheckedChange={() => toggleIntegration(serviceKey, isActive)}
                                data-testid={`toggle-${serviceKey}`}
                              />
                            )}
                          </div>
                          
                          <div className="flex items-center gap-2 mb-3">
                            <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${
                              isConfigured && isActive 
                                ? 'bg-emerald-100 text-emerald-700' 
                                : isConfigured 
                                  ? 'bg-amber-100 text-amber-700'
                                  : 'bg-slate-100 text-slate-600'
                            }`}>
                              {isConfigured && isActive ? (
                                <><CheckCircle2 className="h-3 w-3" /> Connected</>
                              ) : isConfigured ? (
                                <><AlertTriangle className="h-3 w-3" /> Inactive</>
                              ) : (
                                <><XCircle className="h-3 w-3" /> Not Configured</>
                              )}
                            </span>
                            {integration?.lastTestedAt && (
                              <span className="text-xs text-slate-400">
                                Tested: {new Date(integration.lastTestedAt).toLocaleDateString()}
                              </span>
                            )}
                          </div>
                          
                          {isConfigured && (
                            <div className="bg-slate-50 rounded-lg px-3 py-2 mb-3 font-mono text-sm text-slate-600">
                              API Key: {integration.apiKeyMasked}
                            </div>
                          )}
                          
                          <div className="flex gap-2">
                            <Button
                              variant="outline"
                              size="sm"
                              className="flex-1"
                              onClick={() => openIntegrationModal(integration || { service: serviceKey, config: {} })}
                              data-testid={`edit-${serviceKey}`}
                            >
                              <Settings className="h-4 w-4 mr-1" />
                              {isConfigured ? "Edit" : "Configure"}
                            </Button>
                            {isConfigured && isActive && (
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => testIntegration(serviceKey)}
                                disabled={testingIntegration === serviceKey}
                                data-testid={`test-${serviceKey}`}
                              >
                                {testingIntegration === serviceKey ? (
                                  <Loader2 className="h-4 w-4 animate-spin" />
                                ) : (
                                  <Play className="h-4 w-4" />
                                )}
                              </Button>
                            )}
                          </div>
                          
                          {/* Test phone input for Twilio */}
                          {serviceKey === "twilio" && isConfigured && isActive && (
                            <div className="mt-3">
                              <Input
                                placeholder="Phone number for test SMS"
                                value={testPhoneNumber}
                                onChange={(e) => setTestPhoneNumber(e.target.value)}
                                className="text-sm"
                                data-testid="test-phone-input"
                              />
                            </div>
                          )}
                        </CardContent>
                      </Card>
                    );
                  })}
                </div>
              )}
            </Card>
          </TabsContent>

          {/* Users Tab */}
          <TabsContent value="users" className="space-y-4" data-testid="users-panel">
            <Card className="border-slate-200 shadow-none">
              <CardContent className="p-0">
                <div className="p-4 border-b border-slate-200 flex items-center justify-between">
                  <div>
                    <h3 className="font-heading font-bold text-slate-900">Platform Users</h3>
                    <p className="text-sm text-slate-500 mt-0.5">Manage all users across organisations</p>
                  </div>
                  <Button variant="outline" size="sm" onClick={loadUsers} disabled={loadingUsers} data-testid="refresh-users-btn">
                    <RefreshCw className={`h-4 w-4 mr-1.5 ${loadingUsers ? 'animate-spin' : ''}`} />
                    Refresh
                  </Button>
                </div>

                {loadingUsers ? (
                  <div className="p-8 text-center text-slate-500">Loading users...</div>
                ) : allUsers.length === 0 ? (
                  <div className="p-8 text-center text-slate-400">No users found</div>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="text-slate-600 font-semibold">Email</TableHead>
                        <TableHead className="text-slate-600 font-semibold">Name</TableHead>
                        <TableHead className="text-slate-600 font-semibold">Role</TableHead>
                        <TableHead className="text-slate-600 font-semibold">Organisation</TableHead>
                        <TableHead className="text-slate-600 font-semibold">Status</TableHead>
                        <TableHead className="text-slate-600 font-semibold">Last Login</TableHead>
                        <TableHead className="text-slate-600 font-semibold text-right">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {allUsers.map((u) => (
                        <TableRow key={u.id} data-testid={`user-row-${u.id}`}>
                          <TableCell className="font-medium text-slate-900">{u.email}</TableCell>
                          <TableCell className="text-slate-600">{u.name || '—'}</TableCell>
                          <TableCell>
                            <Badge variant="outline" className={
                              u.role === 'platform_owner' ? 'border-violet-300 text-violet-700 bg-violet-50' :
                              u.role === 'owner' ? 'border-blue-300 text-blue-700 bg-blue-50' :
                              'border-emerald-300 text-emerald-700 bg-emerald-50'
                            }>
                              {u.role === 'platform_owner' ? 'Platform' : u.role === 'owner' ? 'Owner' : 'Fundraiser'}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-slate-600 text-sm">{u.organisationName || '—'}</TableCell>
                          <TableCell>
                            {u.state === "retired" ? (
                              <span className="inline-flex items-center gap-1 text-xs font-medium text-slate-600 bg-slate-100 px-2 py-0.5 rounded-full" data-testid={`state-retired-${u.id}`}>
                                <Trash2 className="h-3 w-3" /> Retired
                              </span>
                            ) : u.isDeactivated ? (
                              <span className="inline-flex items-center gap-1 text-xs font-medium text-red-600 bg-red-50 px-2 py-0.5 rounded-full">
                                <XCircle className="h-3 w-3" /> Inactive
                              </span>
                            ) : (
                              <span className="inline-flex items-center gap-1 text-xs font-medium text-emerald-600 bg-emerald-50 px-2 py-0.5 rounded-full">
                                <CheckCircle2 className="h-3 w-3" /> Active
                              </span>
                            )}
                            {u.lifecycleVersion > 1 && (
                              <span className="ml-1 text-[10px] text-slate-400" title="Account lifecycle version">v{u.lifecycleVersion}</span>
                            )}
                          </TableCell>
                          <TableCell className="text-slate-500 text-sm">
                            {u.lastLoginAt ? new Date(u.lastLoginAt).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : 'Never'}
                          </TableCell>
                          <TableCell className="text-right">
                            {u.role !== 'platform_owner' && (
                              u.state === "retired" ? (
                                <div className="flex items-center justify-end gap-1">
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    className="h-8 px-2 text-slate-500 hover:text-emerald-600"
                                    onClick={() => handleReactivateUser(u.id, u.email)}
                                    disabled={userActionLoading === u.id}
                                    title="Reactivate as new account"
                                    data-testid={`reactivate-${u.id}`}
                                  >
                                    <CheckCircle2 className="h-4 w-4 mr-1" /> Reactivate
                                  </Button>
                                </div>
                              ) : (
                              <div className="flex items-center justify-end gap-1">
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="h-8 px-2 text-slate-500 hover:text-blue-600"
                                  onClick={() => handleResetPassword(u.id, u.email)}
                                  disabled={userActionLoading === u.id}
                                  title="Reset Password"
                                  data-testid={`reset-pwd-${u.id}`}
                                >
                                  <KeyRound className="h-4 w-4" />
                                </Button>
                                {u.isDeactivated ? (
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    className="h-8 px-2 text-slate-500 hover:text-emerald-600"
                                    onClick={() => handleActivateUser(u.id, u.email)}
                                    disabled={userActionLoading === u.id}
                                    title="Activate"
                                    data-testid={`activate-${u.id}`}
                                  >
                                    <CheckCircle2 className="h-4 w-4" />
                                  </Button>
                                ) : (
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    className="h-8 px-2 text-slate-500 hover:text-amber-600"
                                    onClick={() => handleDeactivateUser(u.id, u.email)}
                                    disabled={userActionLoading === u.id}
                                    title="Deactivate"
                                    data-testid={`deactivate-${u.id}`}
                                  >
                                    <UserX className="h-4 w-4" />
                                  </Button>
                                )}
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="h-8 px-2 text-slate-500 hover:text-indigo-600"
                                  onClick={() => handleImpersonate(u.id, u.email, u.role)}
                                  disabled={userActionLoading === u.id}
                                  title="Impersonate"
                                  data-testid={`impersonate-${u.id}`}
                                >
                                  <LogIn className="h-4 w-4" />
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="h-8 px-2 text-slate-500 hover:text-red-600"
                                  onClick={() => handleDeleteUser(u.id, u.email)}
                                  disabled={userActionLoading === u.id}
                                  title="Retire (history preserved)"
                                  data-testid={`delete-${u.id}`}
                                >
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                              </div>
                              )
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Enquiries Tab */}
          <TabsContent value="enquiries" className="space-y-4" data-testid="enquiries-panel">
            <Card className="border-slate-200 shadow-none">
              <CardContent className="p-0">
                <div className="p-4 border-b border-slate-200 flex items-center justify-between">
                  <div>
                    <h3 className="font-heading font-bold text-slate-900">Lead Enquiries</h3>
                    <p className="text-sm text-slate-500 mt-0.5">Manage incoming enquiries from the landing page</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button variant="outline" size="sm" onClick={() => setShowExportModal(true)} data-testid="export-enquiries-btn">
                      <Download className="h-4 w-4 mr-1.5" /> Export
                    </Button>
                    <Button variant="outline" size="sm" onClick={loadEnquiries} disabled={loadingEnquiries} data-testid="refresh-enquiries-btn">
                      <RefreshCw className={`h-4 w-4 mr-1.5 ${loadingEnquiries ? 'animate-spin' : ''}`} /> Refresh
                    </Button>
                  </div>
                </div>

                {loadingEnquiries ? (
                  <div className="p-8 text-center text-slate-500">Loading enquiries...</div>
                ) : enquiries.length === 0 ? (
                  <div className="p-8 text-center text-slate-400">No enquiries yet</div>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="w-10">
                          <Checkbox
                            checked={selectedEnquiryIds.length === enquiries.length && enquiries.length > 0}
                            onCheckedChange={toggleAllEnquiries}
                            data-testid="select-all-enquiries"
                          />
                        </TableHead>
                        <TableHead className="text-slate-600 font-semibold">Name</TableHead>
                        <TableHead className="text-slate-600 font-semibold">Email</TableHead>
                        <TableHead className="text-slate-600 font-semibold">Phone</TableHead>
                        <TableHead className="text-slate-600 font-semibold">Organisation</TableHead>
                        <TableHead className="text-slate-600 font-semibold">Status</TableHead>
                        <TableHead className="text-slate-600 font-semibold">Submitted</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {enquiries.map((enq) => (
                        <TableRow
                          key={enq.id}
                          className={`cursor-pointer hover:bg-slate-50 ${enq.status === 'new' ? 'bg-emerald-50/30' : ''}`}
                          onClick={() => { setSelectedEnquiry(enq); setShowEnquiryDetail(true); }}
                          data-testid={`enquiry-row-${enq.id}`}
                        >
                          <TableCell onClick={(e) => e.stopPropagation()}>
                            <Checkbox
                              checked={selectedEnquiryIds.includes(enq.id)}
                              onCheckedChange={() => toggleEnquirySelect(enq.id)}
                            />
                          </TableCell>
                          <TableCell className="font-medium text-slate-900">{enq.name}</TableCell>
                          <TableCell className="text-slate-600">{enq.email}</TableCell>
                          <TableCell className="text-slate-500 text-sm">{enq.phone || '—'}</TableCell>
                          <TableCell className="text-slate-600 text-sm">{enq.organisationName || '—'}</TableCell>
                          <TableCell>
                            <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                              enq.status === 'new' ? 'bg-emerald-100 text-emerald-700' :
                              enq.status === 'contacted' ? 'bg-blue-100 text-blue-700' :
                              enq.status === 'converted' ? 'bg-violet-100 text-violet-700' :
                              'bg-slate-100 text-slate-600'
                            }`} data-testid={`enquiry-status-${enq.id}`}>
                              {enq.status}
                            </span>
                          </TableCell>
                          <TableCell className="text-slate-500 text-sm">
                            {new Date(enq.createdAt).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* Enquiry Detail Modal */}
        <Dialog open={showEnquiryDetail} onOpenChange={setShowEnquiryDetail}>
          <DialogContent className="sm:max-w-lg">
            <DialogHeader>
              <DialogTitle>Enquiry Detail</DialogTitle>
            </DialogHeader>
            {selectedEnquiry && (
              <div className="space-y-4 py-2">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-xs text-slate-500">Name</Label>
                    <p className="font-medium text-slate-900">{selectedEnquiry.name}</p>
                  </div>
                  <div>
                    <Label className="text-xs text-slate-500">Email</Label>
                    <p className="font-medium text-slate-900">{selectedEnquiry.email}</p>
                  </div>
                  <div>
                    <Label className="text-xs text-slate-500">Phone</Label>
                    <p className="text-slate-700">{selectedEnquiry.phone || '—'}</p>
                  </div>
                  <div>
                    <Label className="text-xs text-slate-500">Organisation</Label>
                    <p className="text-slate-700">{selectedEnquiry.organisationName || '—'}</p>
                  </div>
                  <div>
                    <Label className="text-xs text-slate-500">Status</Label>
                    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                      selectedEnquiry.status === 'new' ? 'bg-emerald-100 text-emerald-700' :
                      selectedEnquiry.status === 'contacted' ? 'bg-blue-100 text-blue-700' :
                      selectedEnquiry.status === 'converted' ? 'bg-violet-100 text-violet-700' :
                      'bg-slate-100 text-slate-600'
                    }`}>{selectedEnquiry.status}</span>
                  </div>
                  <div>
                    <Label className="text-xs text-slate-500">Source</Label>
                    <p className="text-slate-600 text-sm">{selectedEnquiry.source || 'landing_page'}</p>
                  </div>
                </div>
                {selectedEnquiry.message && (
                  <div>
                    <Label className="text-xs text-slate-500">Message</Label>
                    <p className="text-slate-700 bg-slate-50 p-3 rounded-lg mt-1 text-sm">{selectedEnquiry.message}</p>
                  </div>
                )}
                <div>
                  <Label className="text-xs text-slate-500">Submitted</Label>
                  <p className="text-slate-600 text-sm">{new Date(selectedEnquiry.createdAt).toLocaleString('en-GB')}</p>
                </div>
                <div>
                  <Label className="text-xs text-slate-500">Internal Notes</Label>
                  <Textarea
                    className="mt-1"
                    rows={2}
                    placeholder="Add internal notes..."
                    defaultValue={selectedEnquiry.notes || ""}
                    onBlur={async (e) => {
                      const val = e.target.value;
                      if (val !== (selectedEnquiry.notes || "")) {
                        try {
                          await apiClient.put(`/platform/enquiries/${selectedEnquiry.id}/status`, { notes: val });
                          setSelectedEnquiry({ ...selectedEnquiry, notes: val });
                          toast.success("Notes saved");
                        } catch { toast.error("Failed to save notes"); }
                      }
                    }}
                    data-testid="enquiry-notes"
                  />
                </div>
                <div className="flex gap-2 pt-2 border-t">
                  <Button size="sm" variant="outline"
                    onClick={() => handleEnquiryStatus(selectedEnquiry.id, "contacted")}
                    disabled={enquiryActionLoading === selectedEnquiry.id || selectedEnquiry.status === "contacted"}
                    data-testid="mark-contacted-btn">
                    Mark Contacted
                  </Button>
                  <Button size="sm" variant="outline" className="text-violet-600 border-violet-200"
                    onClick={() => handleEnquiryStatus(selectedEnquiry.id, "converted")}
                    disabled={enquiryActionLoading === selectedEnquiry.id || selectedEnquiry.status === "converted"}
                    data-testid="mark-converted-btn">
                    Mark Converted
                  </Button>
                  <Button size="sm" variant="destructive"
                    onClick={() => handleDeleteEnquiry(selectedEnquiry.id)}
                    disabled={enquiryActionLoading === selectedEnquiry.id}
                    data-testid="delete-enquiry-btn">
                    <Trash2 className="h-3.5 w-3.5 mr-1" /> Delete
                  </Button>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Export Modal */}
        <Dialog open={showExportModal} onOpenChange={setShowExportModal}>
          <DialogContent className="sm:max-w-sm">
            <DialogHeader>
              <DialogTitle>Export Enquiries</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-2">
              {selectedEnquiryIds.length > 0 && (
                <Button variant="outline" className="w-full justify-start" onClick={() => handleExportEnquiries("selected")} data-testid="export-selected-btn">
                  <Download className="h-4 w-4 mr-2" />
                  Export Selected ({selectedEnquiryIds.length})
                </Button>
              )}
              <div className="space-y-3">
                <p className="text-sm font-medium text-slate-700">Export by Date Range</p>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <Label className="text-xs">From</Label>
                    <Input type="date" value={enquiryExportDateFrom} onChange={(e) => setEnquiryExportDateFrom(e.target.value)} data-testid="export-date-from" />
                  </div>
                  <div>
                    <Label className="text-xs">To</Label>
                    <Input type="date" value={enquiryExportDateTo} onChange={(e) => setEnquiryExportDateTo(e.target.value)} data-testid="export-date-to" />
                  </div>
                </div>
                <Button variant="outline" className="w-full justify-start" onClick={() => handleExportEnquiries("dateRange")} data-testid="export-date-range-btn">
                  <Download className="h-4 w-4 mr-2" />
                  Export by Date Range
                </Button>
              </div>
              <Button variant="outline" className="w-full justify-start" onClick={() => handleExportEnquiries("all")} data-testid="export-all-btn">
                <Download className="h-4 w-4 mr-2" />
                Export All
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* Integration Edit Modal */}
        <Dialog open={showIntegrationModal} onOpenChange={setShowIntegrationModal}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Plug className="h-5 w-5 text-emerald-600" />
                Configure {INTEGRATION_SERVICES[integrationForm.service]?.name || integrationForm.service}
              </DialogTitle>
            </DialogHeader>
            
            <div className="space-y-4 py-4">
              {/* Generic API Key field - NOT shown for Stripe (uses specific fields instead) */}
              {integrationForm.service !== "stripe" && (
              <div>
                <Label className="text-slate-700 font-medium">API Key *</Label>
                <div className="relative mt-1.5">
                  <Input
                    type={showApiKey ? "text" : "password"}
                    value={integrationForm.apiKey}
                    onChange={(e) => setIntegrationForm({ ...integrationForm, apiKey: e.target.value })}
                    placeholder={selectedIntegration?.apiKeyMasked || "Enter API key"}
                    className="pr-10"
                    data-testid="integration-api-key-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowApiKey(!showApiKey)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                  >
                    {showApiKey ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
                <p className="text-xs text-slate-500 mt-1">
                  {selectedIntegration?.apiKeyMasked ? "Leave empty to keep existing key, or enter new key to update" : "Enter your API key"}
                </p>
              </div>
              )}
              
              {/* Stripe-specific fields */}
              {integrationForm.service === "stripe" && (
                <>
                  <div>
                    <Label className="text-slate-700 font-medium">Stripe Secret Key *</Label>
                    <div className="relative mt-1.5">
                      <Input
                        type={showApiKey ? "text" : "password"}
                        value={integrationForm.config.stripeSecretKey || ""}
                        onChange={(e) => setIntegrationForm({
                          ...integrationForm,
                          config: { ...integrationForm.config, stripeSecretKey: e.target.value }
                        })}
                        placeholder="sk_test_..."
                        className="pr-10"
                        data-testid="stripe-secret-key-input"
                      />
                      <button
                        type="button"
                        onClick={() => setShowApiKey(!showApiKey)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                      >
                        {showApiKey ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                      </button>
                    </div>
                    <p className="text-xs text-slate-500 mt-1">
                      {selectedIntegration?.config?.stripeSecretKey ? "Leave empty to keep existing key" : "Required for all Stripe API calls"}
                    </p>
                  </div>
                  <div>
                    <Label className="text-slate-700 font-medium">Stripe Publishable Key</Label>
                    <Input
                      value={integrationForm.config.stripePublishableKey || ""}
                      onChange={(e) => setIntegrationForm({
                        ...integrationForm,
                        config: { ...integrationForm.config, stripePublishableKey: e.target.value }
                      })}
                      placeholder="pk_test_..."
                      className="mt-1.5"
                      data-testid="stripe-publishable-key-input"
                    />
                    <p className="text-xs text-slate-500 mt-1">Optional — used for frontend Stripe integrations</p>
                  </div>
                  <div>
                    <Label className="text-slate-700 font-medium">Stripe Webhook Secret</Label>
                    <div className="relative mt-1.5">
                      <Input
                        type={showApiKey ? "text" : "password"}
                        value={integrationForm.config.stripeWebhookSecret || ""}
                        onChange={(e) => setIntegrationForm({
                          ...integrationForm,
                          config: { ...integrationForm.config, stripeWebhookSecret: e.target.value }
                        })}
                        placeholder="whsec_..."
                        className="pr-10"
                        data-testid="stripe-webhook-secret-input"
                      />
                      <button
                        type="button"
                        onClick={() => setShowApiKey(!showApiKey)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                      >
                        {showApiKey ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                      </button>
                    </div>
                    <p className="text-xs text-slate-500 mt-1">Required for production — verifies incoming webhook events</p>
                    {!integrationForm.config.stripeWebhookSecret && (
                      <div className="mt-2 p-2 rounded-lg bg-amber-50 border border-amber-200">
                        <p className="text-xs text-amber-700 flex items-center gap-1">
                          <AlertTriangle className="h-3 w-3" />
                          Webhook verification will be skipped without this secret
                        </p>
                      </div>
                    )}
                  </div>
                </>
              )}
              
              {/* Service-specific config fields */}
              {integrationForm.service === "twilio" && (
                <>
                  <div>
                    <Label className="text-slate-700 font-medium">Account SID</Label>
                    <Input
                      value={integrationForm.config.accountSid || ""}
                      onChange={(e) => setIntegrationForm({ 
                        ...integrationForm, 
                        config: { ...integrationForm.config, accountSid: e.target.value }
                      })}
                      placeholder="ACxxxxxxxxxxxxxxx"
                      className="mt-1.5"
                      data-testid="twilio-account-sid"
                    />
                  </div>
                  <div>
                    <Label className="text-slate-700 font-medium">Auth Token</Label>
                    <Input
                      type="password"
                      value={integrationForm.config.authToken || ""}
                      onChange={(e) => setIntegrationForm({ 
                        ...integrationForm, 
                        config: { ...integrationForm.config, authToken: e.target.value }
                      })}
                      placeholder="Your auth token"
                      className="mt-1.5"
                      data-testid="twilio-auth-token"
                    />
                  </div>
                  <div>
                    <Label className="text-slate-700 font-medium">Phone Number (From)</Label>
                    <Input
                      value={integrationForm.config.phoneNumber || ""}
                      onChange={(e) => setIntegrationForm({ 
                        ...integrationForm, 
                        config: { ...integrationForm.config, phoneNumber: e.target.value }
                      })}
                      placeholder="+447xxxxxxxxx"
                      className="mt-1.5"
                      data-testid="twilio-phone-number"
                    />
                  </div>
                </>
              )}
              
              {integrationForm.service === "resend" && (
                <div>
                  <Label className="text-slate-700 font-medium">Sender Email</Label>
                  <Input
                    type="email"
                    value={integrationForm.config.senderEmail || ""}
                    onChange={(e) => setIntegrationForm({ 
                      ...integrationForm, 
                      config: { ...integrationForm.config, senderEmail: e.target.value }
                    })}
                    placeholder="noreply@yourdomain.com"
                    className="mt-1.5"
                    data-testid="resend-sender-email"
                  />
                  <p className="text-xs text-slate-500 mt-1">Default: onboarding@resend.dev</p>
                </div>
              )}
              
              {integrationForm.service === "postcode_lookup" && (
                <div>
                  <Label className="text-slate-700 font-medium">Provider</Label>
                  <Select 
                    value={integrationForm.config.provider || "postcodes.io"} 
                    onValueChange={(v) => setIntegrationForm({ 
                      ...integrationForm, 
                      config: { ...integrationForm.config, provider: v }
                    })}
                  >
                    <SelectTrigger className="mt-1.5" data-testid="postcode-provider-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="postcodes.io">Postcodes.io (Free)</SelectItem>
                      <SelectItem value="getaddress.io">GetAddress.io (Paid)</SelectItem>
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-slate-500 mt-1">
                    postcodes.io is free but provides limited address data
                  </p>
                </div>
              )}
              
              {integrationForm.service === "bank_validation" && (
                <div className="bg-slate-50 rounded-lg p-4 border border-slate-200">
                  <div className="flex items-center gap-2 text-slate-700">
                    <AlertTriangle className="h-4 w-4" />
                    <span className="text-sm font-medium">Not part of the current release</span>
                  </div>
                  <p className="text-xs text-slate-500 mt-1">
                    Bank validation is available as a future optional integration and does not affect current MVP workflows.
                  </p>
                </div>
              )}
            </div>
            
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowIntegrationModal(false)}>
                Cancel
              </Button>
              <Button
                onClick={saveIntegration}
                disabled={savingIntegration}
                className="bg-emerald-500 hover:bg-emerald-600"
                data-testid="save-integration-btn"
              >
                {savingIntegration ? "Saving..." : "Save Integration"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Pricing Config Dialog (reused) */}
        <Dialog open={showPricingConfig} onOpenChange={setShowPricingConfig}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Calculator className="h-5 w-5 text-emerald-600" />
                One-Off Pricing Config
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="bg-slate-50 rounded-lg p-3 mb-2">
                <span className="text-sm text-slate-600">Organisation: </span>
                <span className="font-medium text-slate-900">{selectedOrg?.name}</span>
              </div>

              <div>
                <Label className="text-slate-700 font-medium">Pricing Type</Label>
                <Select 
                  value={pricingConfig.oneOff_pricingType} 
                  onValueChange={(v) => setPricingConfig({ ...pricingConfig, oneOff_pricingType: v })}
                >
                  <SelectTrigger className="mt-1.5">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="percentage">Percentage</SelectItem>
                    <SelectItem value="fixed">Fixed Fee</SelectItem>
                    <SelectItem value="hybrid">Hybrid (Max of % or Fixed)</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {(pricingConfig.oneOff_pricingType === "percentage" || pricingConfig.oneOff_pricingType === "hybrid") && (
                <div>
                  <Label className="text-slate-700 font-medium">Percentage Fee (%)</Label>
                  <NumericInput
                    step="0.01"
                    min="0"
                    fallback={0}
                    value={Number((pricingConfig.oneOff_percentageFee * 100).toFixed(2))}
                    onValueChange={(v) => setPricingConfig({ ...pricingConfig, oneOff_percentageFee: v === "" ? "" : v / 100 })}
                    className="mt-1.5"
                  />
                </div>
              )}

              {(pricingConfig.oneOff_pricingType === "fixed" || pricingConfig.oneOff_pricingType === "hybrid") && (
                <div>
                  <Label className="text-slate-700 font-medium">Fixed Fee (£)</Label>
                  <NumericInput
                    step="0.01"
                    min="0"
                    fallback={0}
                    value={pricingConfig.oneOff_fixedFee}
                    onValueChange={(v) => setPricingConfig({ ...pricingConfig, oneOff_fixedFee: v })}
                    className="mt-1.5"
                  />
                </div>
              )}

              <div className="bg-emerald-50 rounded-lg p-4 border border-emerald-200">
                <div className="flex items-center gap-2 mb-3">
                  <Calculator className="h-4 w-4 text-emerald-600" />
                  <span className="font-medium text-emerald-700">Fee Preview</span>
                </div>
                <div className="flex items-center gap-2 mb-2">
                  <span className="text-sm text-slate-600">If donation is £</span>
                  <NumericInput
                    fallback={0}
                    value={previewAmount}
                    onValueChange={(v) => setPreviewAmount(v)}
                    className="w-20 h-8 text-center"
                  />
                </div>
                <div className="text-lg font-heading font-bold text-emerald-700">
                  → Fee = £{calculatePreview().toFixed(2)}
                </div>
              </div>

              <Button
                onClick={savePricingConfig}
                disabled={savingPricing}
                className="w-full bg-emerald-500 hover:bg-emerald-600"
              >
                {savingPricing ? "Saving..." : "Save Pricing Configuration"}
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* Metric Drill-down Modal */}
        <Dialog open={showMetricDrilldown} onOpenChange={setShowMetricDrilldown}>
          <DialogContent className="sm:max-w-2xl max-h-[85vh] overflow-hidden flex flex-col">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                {drilldownType === 'revenue' && <><DollarSign className="h-5 w-5 text-emerald-600" /> Revenue Breakdown</>}
                {drilldownType === 'conversions' && <><Target className="h-5 w-5 text-blue-600" /> Conversions Breakdown</>}
                {drilldownType === 'signups' && <><Building2 className="h-5 w-5 text-violet-600" /> Organisation Status</>}
              </DialogTitle>
              <DialogDescription>
                Detailed breakdown by organisation
              </DialogDescription>
            </DialogHeader>
            
            {loadingDrilldown ? (
              <div className="py-12 text-center text-slate-500">Loading data...</div>
            ) : drilldownData ? (
              <div className="flex-1 overflow-y-auto space-y-4">
                {/* Summary */}
                <div className="bg-slate-50 p-4 rounded-lg">
                  {drilldownType === 'revenue' && (
                    <div className="text-center">
                      <div className="text-3xl font-heading font-black text-emerald-600">
                        £{(drilldownData.total || 0).toLocaleString()}
                      </div>
                      <div className="text-sm text-slate-500 mt-1">Total Revenue</div>
                    </div>
                  )}
                  {drilldownType === 'conversions' && (
                    <div className="text-center">
                      <div className="text-3xl font-heading font-black text-blue-600">
                        {(drilldownData.total || 0).toLocaleString()}
                      </div>
                      <div className="text-sm text-slate-500 mt-1">Total Conversions</div>
                    </div>
                  )}
                  {drilldownType === 'signups' && (
                    <div className="grid grid-cols-2 gap-4 text-center">
                      <div>
                        <div className="text-2xl font-heading font-black text-emerald-600">{drilldownData.active}</div>
                        <div className="text-sm text-slate-500">Active</div>
                      </div>
                      <div>
                        <div className="text-2xl font-heading font-black text-red-600">{drilldownData.restricted}</div>
                        <div className="text-sm text-slate-500">Restricted</div>
                      </div>
                    </div>
                  )}
                </div>
                
                {/* Breakdown Table */}
                <div className="border rounded-xl overflow-hidden">
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-slate-50">
                        <TableHead>Organisation</TableHead>
                        {drilldownType === 'revenue' && <><TableHead>Plan</TableHead><TableHead className="text-right">Revenue</TableHead><TableHead className="text-right">Conversions</TableHead></>}
                        {drilldownType === 'conversions' && <><TableHead className="text-right">Conversions</TableHead><TableHead className="text-right">Usage</TableHead></>}
                        {drilldownType === 'signups' && <><TableHead>Plan</TableHead><TableHead>Status</TableHead></>}
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {(drilldownData.items || []).map((item, idx) => (
                        <TableRow key={item.id || item.name || `drill-${idx}`}>
                          <TableCell className="font-medium">{item.name}</TableCell>
                          {drilldownType === 'revenue' && (
                            <>
                              <TableCell><Badge variant="outline">{item.plan}</Badge></TableCell>
                              <TableCell className="text-right font-semibold text-emerald-600">£{(item.revenue || 0).toLocaleString()}</TableCell>
                              <TableCell className="text-right">{item.conversions || 0}</TableCell>
                            </>
                          )}
                          {drilldownType === 'conversions' && (
                            <>
                              <TableCell className="text-right font-semibold text-blue-600">{item.conversions || 0}</TableCell>
                              <TableCell className="text-right">{item.usageCount || 0}</TableCell>
                            </>
                          )}
                          {drilldownType === 'signups' && (
                            <>
                              <TableCell><Badge variant="outline">{item.plan}</Badge></TableCell>
                              <TableCell>
                                <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${
                                  item.status === 'active' ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'
                                }`}>
                                  {item.status === 'active' ? <CheckCircle className="h-3 w-3" /> : <XCircle className="h-3 w-3" />}
                                  {item.status}
                                </span>
                              </TableCell>
                            </>
                          )}
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </div>
            ) : (
              <div className="py-12 text-center text-slate-500">No records yet for this metric.</div>
            )}
            
            <DialogFooter className="pt-4 border-t">
              <Button variant="outline" onClick={() => setShowMetricDrilldown(false)}>
                Close
              </Button>
              <Button onClick={exportDrilldownData} className="bg-emerald-500 hover:bg-emerald-600">
                <Download className="h-4 w-4 mr-2" />
                Export CSV
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Create Pricing Plan Modal */}
        <Dialog open={showCreatePlanModal} onOpenChange={setShowCreatePlanModal}>
          <DialogContent className="sm:max-w-lg">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <DollarSign className="h-5 w-5 text-emerald-600" />
                Create Pricing Plan
              </DialogTitle>
              <DialogDescription>
                Define a new subscription plan for organisations
              </DialogDescription>
            </DialogHeader>
            
            <div className="space-y-4 py-4 max-h-[60vh] overflow-y-auto">
              <div>
                <Label className="text-slate-700 font-medium">Plan Name *</Label>
                <Input
                  value={planForm.name}
                  onChange={(e) => setPlanForm({...planForm, name: e.target.value})}
                  placeholder="e.g. Growth, Pro, Enterprise"
                  className="mt-1.5"
                />
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-slate-700 font-medium">Monthly Price (£) *</Label>
                  <NumericInput
                    step="0.01"
                    min="0"
                    fallback={0}
                    value={planForm.monthlyPrice}
                    onValueChange={(v) => setPlanForm({...planForm, monthlyPrice: v})}
                    className="mt-1.5"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-slate-700 font-medium">Included Users</Label>
                  <NumericInput
                    integer
                    min="0"
                    fallback={0}
                    value={planForm.maxUsers}
                    onValueChange={(v) => setPlanForm({...planForm, maxUsers: v})}
                    placeholder="0 = unlimited"
                    className="mt-1.5"
                    data-testid="create-plan-max-users"
                  />
                  <p className="text-xs text-slate-500 mt-1">0 means unlimited</p>
                </div>
                <div>
                  <Label className="text-slate-700 font-medium">Extra User Price (£)</Label>
                  <NumericInput
                    step="0.01"
                    min="0"
                    fallback={0}
                    value={planForm.pricePerUser}
                    onValueChange={(v) => setPlanForm({...planForm, pricePerUser: v})}
                    className="mt-1.5"
                    data-testid="create-plan-price-per-user"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-slate-700 font-medium">Interactions per User</Label>
                  <NumericInput
                    integer
                    min="0"
                    fallback={0}
                    value={planForm.interactionsPerUser}
                    onValueChange={(v) => setPlanForm({...planForm, interactionsPerUser: v})}
                    className="mt-1.5"
                    data-testid="create-plan-interactions-per-user"
                  />
                </div>
                <div>
                  <Label className="text-slate-700 font-medium">Extra User Interactions</Label>
                  <NumericInput
                    integer
                    min="0"
                    fallback={0}
                    value={planForm.extraUserInteractions}
                    onValueChange={(v) => setPlanForm({...planForm, extraUserInteractions: v})}
                    placeholder="Defaults to Interactions per User"
                    className="mt-1.5"
                    data-testid="create-plan-extra-user-interactions"
                  />
                  <p className="text-xs text-slate-500 mt-1">0 = same as Interactions per User</p>
                </div>
              </div>

              <div>
                <Label className="text-slate-700 font-medium">Overage Price per Signup (£)</Label>
                <NumericInput
                  step="0.01"
                  min="0"
                  fallback={0}
                  value={planForm.pricePerSignup}
                  onValueChange={(v) => setPlanForm({...planForm, pricePerSignup: v})}
                  className="mt-1.5"
                  data-testid="create-plan-overage-price"
                />
              </div>

              <div className="p-3 bg-slate-50 rounded-lg border border-slate-200">
                <div className="text-xs font-semibold text-slate-500 uppercase mb-1">Derived capacity</div>
                <div className="text-sm text-slate-700">
                  Total included interactions: <span className="font-bold text-emerald-700" data-testid="create-plan-total-included">{((planForm.maxUsers || 0) * (planForm.interactionsPerUser || 0)).toLocaleString()}</span>
                </div>
              </div>

              <div className="flex items-center gap-2">
                <Switch
                  checked={planForm.chargePerUser}
                  onCheckedChange={(v) => setPlanForm({...planForm, chargePerUser: v})}
                  data-testid="create-plan-charge-per-user"
                />
                <Label className="text-slate-700">Enable per-user charging</Label>
              </div>
              
              <div>
                <Label className="text-slate-700 font-medium">Features (comma separated)</Label>
                <Input
                  value={featuresInput}
                  onChange={(e) => setFeaturesInput(e.target.value)}
                  placeholder="e.g. Unlimited campaigns, SMS enabled, White labelling"
                  className="mt-1.5"
                />
                <p className="text-xs text-slate-500 mt-1">Enter features separated by commas</p>
              </div>
              
              <div className="p-3 bg-emerald-50 rounded-lg border border-emerald-200">
                <p className="text-sm text-emerald-700 font-medium">Usage-Based Model</p>
                <p className="text-xs text-emerald-600 mt-1">All plans include full platform access. Pricing differs only by included usage and overage costs.</p>
              </div>
              
              <div className="flex items-center gap-2">
                <Switch
                  checked={planForm.isActive}
                  onCheckedChange={(v) => setPlanForm({...planForm, isActive: v})}
                />
                <Label className="text-slate-700">Active (visible to organisations)</Label>
              </div>
              <div className="flex items-center gap-2">
                <Switch
                  checked={planForm.isPopular}
                  onCheckedChange={(v) => setPlanForm({...planForm, isPopular: v})}
                />
                <Label className="text-slate-700">Popular (highlighted on landing page)</Label>
              </div>
            </div>
            
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowCreatePlanModal(false)}>
                Cancel
              </Button>
              <Button 
                onClick={handleCreatePlan} 
                disabled={creatingPlan || !planForm.name}
                className="bg-emerald-500 hover:bg-emerald-600"
              >
                {creatingPlan ? "Creating..." : "Create Plan"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Edit Pricing Plan Modal */}
        <Dialog open={showEditPlanModal} onOpenChange={setShowEditPlanModal}>
          <DialogContent className="sm:max-w-lg">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Pencil className="h-5 w-5 text-blue-600" />
                Edit Pricing Plan
              </DialogTitle>
              <DialogDescription>
                Update plan details for "{editingPlan?.name}"
              </DialogDescription>
            </DialogHeader>
            
            <div className="space-y-4 py-4 max-h-[60vh] overflow-y-auto">
              <div>
                <Label className="text-slate-700 font-medium">Plan Name *</Label>
                <Input
                  value={planForm.name}
                  onChange={(e) => setPlanForm({...planForm, name: e.target.value})}
                  placeholder="e.g. Growth, Pro, Enterprise"
                  className="mt-1.5"
                />
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-slate-700 font-medium">Monthly Price (£) *</Label>
                  <NumericInput
                    step="0.01"
                    min="0"
                    fallback={0}
                    value={planForm.monthlyPrice}
                    onValueChange={(v) => setPlanForm({...planForm, monthlyPrice: v})}
                    className="mt-1.5"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-slate-700 font-medium">Included Users</Label>
                  <NumericInput
                    integer
                    min="0"
                    fallback={0}
                    value={planForm.maxUsers}
                    onValueChange={(v) => setPlanForm({...planForm, maxUsers: v})}
                    placeholder="0 = unlimited"
                    className="mt-1.5"
                    data-testid="edit-plan-max-users"
                  />
                  <p className="text-xs text-slate-500 mt-1">0 means unlimited</p>
                </div>
                <div>
                  <Label className="text-slate-700 font-medium">Extra User Price (£)</Label>
                  <NumericInput
                    step="0.01"
                    min="0"
                    fallback={0}
                    value={planForm.pricePerUser}
                    onValueChange={(v) => setPlanForm({...planForm, pricePerUser: v})}
                    className="mt-1.5"
                    data-testid="edit-plan-price-per-user"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-slate-700 font-medium">Interactions per User</Label>
                  <NumericInput
                    integer
                    min="0"
                    fallback={0}
                    value={planForm.interactionsPerUser}
                    onValueChange={(v) => setPlanForm({...planForm, interactionsPerUser: v})}
                    className="mt-1.5"
                    data-testid="edit-plan-interactions-per-user"
                  />
                </div>
                <div>
                  <Label className="text-slate-700 font-medium">Extra User Interactions</Label>
                  <NumericInput
                    integer
                    min="0"
                    fallback={0}
                    value={planForm.extraUserInteractions}
                    onValueChange={(v) => setPlanForm({...planForm, extraUserInteractions: v})}
                    placeholder="Defaults to Interactions per User"
                    className="mt-1.5"
                    data-testid="edit-plan-extra-user-interactions"
                  />
                  <p className="text-xs text-slate-500 mt-1">0 = same as Interactions per User</p>
                </div>
              </div>

              <div>
                <Label className="text-slate-700 font-medium">Overage Price per Signup (£)</Label>
                <NumericInput
                  step="0.01"
                  min="0"
                  fallback={0}
                  value={planForm.pricePerSignup}
                  onValueChange={(v) => setPlanForm({...planForm, pricePerSignup: v})}
                  className="mt-1.5"
                  data-testid="edit-plan-overage-price"
                />
              </div>

              <div className="p-3 bg-slate-50 rounded-lg border border-slate-200">
                <div className="text-xs font-semibold text-slate-500 uppercase mb-1">Derived capacity</div>
                <div className="text-sm text-slate-700">
                  Total included interactions: <span className="font-bold text-emerald-700" data-testid="edit-plan-total-included">{((planForm.maxUsers || 0) * (planForm.interactionsPerUser || 0)).toLocaleString()}</span>
                </div>
              </div>

              <div className="flex items-center gap-2">
                <Switch
                  checked={planForm.chargePerUser}
                  onCheckedChange={(v) => setPlanForm({...planForm, chargePerUser: v})}
                  data-testid="edit-plan-charge-per-user"
                />
                <Label className="text-slate-700">Enable per-user charging</Label>
              </div>
              
              <div>
                <Label className="text-slate-700 font-medium">Features (comma separated)</Label>
                <Input
                  value={featuresInput}
                  onChange={(e) => setFeaturesInput(e.target.value)}
                  placeholder="e.g. Unlimited campaigns, SMS enabled, White labelling"
                  className="mt-1.5"
                />
              </div>
              
              <div className="p-3 bg-emerald-50 rounded-lg border border-emerald-200">
                <p className="text-sm text-emerald-700 font-medium">Usage-Based Model</p>
                <p className="text-xs text-emerald-600 mt-1">All plans include full platform access. Pricing differs only by included usage and overage costs.</p>
              </div>
              
              <div className="flex items-center gap-2">
                <Switch
                  checked={planForm.isActive}
                  onCheckedChange={(v) => setPlanForm({...planForm, isActive: v})}
                />
                <Label className="text-slate-700">Active (visible to organisations)</Label>
              </div>
              <div className="flex items-center gap-2">
                <Switch
                  checked={planForm.isPopular}
                  onCheckedChange={(v) => setPlanForm({...planForm, isPopular: v})}
                />
                <Label className="text-slate-700">Popular (highlighted on landing page)</Label>
              </div>
            </div>
            
            <DialogFooter>
              <Button variant="outline" onClick={() => { setShowEditPlanModal(false); setEditingPlan(null); }}>
                Cancel
              </Button>
              <Button 
                onClick={handleEditPlan} 
                disabled={creatingPlan || !planForm.name}
                className="bg-blue-500 hover:bg-blue-600"
              >
                {creatingPlan ? "Saving..." : "Save Changes"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </main>
    </div>
  );
}
